// Экран «Работы» (вкладка сверху «Работы», шаг 1 из 5, 2026-10-16; поведение — по эскизу,
// проверенному владельцем). Одна таблица: структура работ проекта (своя разбивка = иерархия
// проекта, разделы — строки с детьми), дни на типовой этаж, связи тегами («Предшественники» /
// «Последователи») и справа Гант одного типового этажа с критическим путём.
// Данные и сохранение структуры — хук `useIerarhiya` (Ierarhiya.tsx): правка целиком через
// PUT /wbs с задержкой, новая строка без детей = новая работа (сервер создаёт её сам и
// наследует раскатку, дни, порядок, календарь у предыдущей работы раздела). Связи — строки
// шаблонов связей проекта (их сумма = связи графика), по именам работ.
// Несколько своих разбивок (шаг 5, 2026-10-16; владелец: «раздел — это условность, по сути
// признак; хочу несколько своих разбивок, добавил — появилась в перечне»): в переключателе
// слева — разбивки проекта по названиям (основная «Этапы» и свои), «＋ своя разбивка» заводит
// новую со всеми работами списком; у открытой своей — «×» удалить, двойной щелчок — переименовать.
// Работы, связи, дни общие; у разбивки свои заголовки и порядок (хук получает id разбивки, PUT
// /wbs?razbivka=). В «Параметрах» после «Контур» — столбец на каждую разбивку: раздел, где стоит
// работа; выбор переносит её (и выделенные) в конец раздела той разбивки.
// Фильтры панели сверху экран не применяет — показываются все работы.
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { call, p, type Data } from '../api';
import { ask, confirmBox } from '../dialog';
import type { ShellCtx } from '../shell';
import { useIerarhiya, estDeti, konecPoddereva, roditel, type IerStroka } from './Ierarhiya';
import { RASK_LABEL } from './Works';
import { Spravochniki } from '../Spravochniki';
import { at as yakorPop, type Anchor } from '../Pop';

type Tpl = Data<'/api/link-templates', 'get'>[number];
type TplRow = Data<'/api/link-templates/{tid}/rows', 'get'>[number];
type Razbivka = Data<'/api/projects/{pid}/razbivki', 'get'>[number];
type Uzel = Data<'/api/projects/{pid}/wbs', 'get'>['uzly'][number];
/** раздел разбивки для списка в «Параметрах»: ключ (id узла или ключ строки), путь «Раздел / Подраздел» */
type Razdel = { k: string; put: string };
/** связь графика: pred → work; id — строка шаблона (для удаления), отрицательный — ещё сохраняется */
type Svyaz = { id: number; work: string; pred: string; tip: string; zapas: number; oper: number; soft: boolean };
type Kuda = 'verh' | 'niz' | 'vnutr';
type Rasch = { es: number; ef: number; ved: string | null; lf: number; zapas: number; krit: boolean };
type Rabota = Data<'/api/projects/{pid}/works', 'get'>[number];

/** группировки строк (шаг 4, 2026-10-16; эскиз: STRUKTURY). Свои разбивки проекта (`rz:<id>`,
    основная «Этапы» и заведённые планером) — иерархия; остальные строятся сами из признака работы:
    pole — поле в ответе /works, patch — в PATCH /works, kod — список в «Списках» (оттуда и пустые
    группы, в которые можно перетащить) */
const GRUPPY = [
  { k: 'sistema', t: 'Группа работ', pole: 'sistema', patch: 'uroven2', kod: 'gruppa' },
  { k: 'mesto', t: 'Место', pole: 'mesto', patch: 'mesto', kod: 'mesto' },
  { k: 'kontur', t: 'Контур', pole: 'kontur', patch: 'kontur', kod: 'kontur' },
  // строка ГПР — единица планирования (TZ_STROKI_GPR.md, 2026-10-26): работы группы получат одни сроки
  // на этаже (расчёт — шаг 2). Строки не в «Списках»: сервер заводит строку по названию при переносе
  { k: 'stroka', t: 'Строка в ГПР', pole: 'stroka_gpr', patch: 'stroka_gpr', kod: 'stroka_gpr' },
  { k: 'net', t: 'Без группировки' },
] as const;
/** открыта своя разбивка — группировки по признаку нет */
const SVOYA = { k: 'svoya', t: 'Разбивка' } as const;
type GrupK = string;   // 'rz:<id разбивки>' | ключ из GRUPPY
type Gruppa = { k: string; val: string | null; name: string; idx: number[] };
/** строка на экране: строка иерархии (номер в R) или заголовок группы */
/** glav — работа, ставшая строкой ГПР после «Разбить»: рисуется вместо заголовка группы (2026-10-26) */
type Pokaz = { gr: null; i: number; glav?: Gruppa } | { gr: Gruppa; i: -1 };
const GRUP_LS = 'rb_gruppirovka';
const RZ = 'rz:';
/** вид экрана (шаг 3, 2026-10-16; эскиз: vidT): «Связи и график» — теги и Гант, «Параметры» — поля работ столбцами */
type Vid = 'svyazi' | 'gpr' | 'otch';   // «Гантт» | «ГПР» — влияет на расчёт | «Отчёты» — группировки и фильтры (2026-10-16)
const VID_LS = 'rb_vid';
/** столбцы «Параметров»: поле в ответе /works → поле PATCH /works; kod — список значений в «Списках» */
const POLYA = [
  // «ГПР» — всё, что влияет на расчёт графика (владелец 2026-10-16)
  { k: 'raskatka', t: 'Раскатка', patch: 'raskatka', vid: 'gpr', title: 'Раскатка работы: по каким этажам или корпусам создаются её задачи' },
  { k: 'logika', t: 'Порядок этажей', patch: 'logika_montazha', vid: 'gpr', title: 'Порядок этажей (логика монтажа): в какой очерёдности бригада идёт по этажам' },
  { k: 'kalendar', t: 'Календарь', patch: 'kalendar', vid: 'gpr', title: 'Календарь работы: календарные дни или без государственных праздников' },
  { k: 'korpusa', t: 'Корпуса', patch: 'korpusa', vid: 'gpr', title: 'На каких корпусах идёт работа: через запятую, пусто — на всех' },
  // строка ГПР (владелец 2026-10-26): в какой суммарной позиции графика стоит работа; пусто — сама себе строка
  { k: 'stroka_gpr', t: 'Работа ГПР', patch: 'stroka_gpr', vid: 'gpr', title: 'Строка ГПР — суммарная позиция графика, в которой стоит работа (сроки у работ строки общие); пусто — работа сама себе строка' },
  // «Отчёты» — признаки для группировок и фильтров
  { k: 'sistema', t: 'Группа работ', patch: 'uroven2', kod: 'gruppa', vid: 'otch', title: 'Группа работ (инженерная система и т.п.) — список «Группы работ» в «Списках»' },
  { k: 'mesto', t: 'Место', patch: 'mesto', kod: 'mesto', vid: 'otch', title: 'Место работы — список «Место» в «Списках»' },
  { k: 'kontur', t: 'Контур', patch: 'kontur', kod: 'kontur', vid: 'otch', title: 'Контур планирования работы — список «Контур» в «Списках»' },
] as const;
const KAL_LABEL: Record<string, string> = { 'календарные': 'Календарные дни', 'без праздников': 'Без праздников' };
type PoleK = typeof POLYA[number]['k'];
/** служебное значение пункта «＋ новое значение…» в списках признаков */
const NOVOE = '__novoe__';

/** строка на экране — только с названием (пустая строка хука внизу не показывается) */
const nastoyashchie = (rs: IerStroka[]) => rs.filter(r => r.name.trim() !== '');
/** имя работы, под которым её знают связи (пока новое имя не сохранено — прежнее) */
const imyaSv = (r: IerStroka) => r.imyaSohr || r.name.trim();
/** уровни без «прыжков»: первая строка — 0, каждая следующая не глубже предыдущей + 1 */
function norm(rs: IerStroka[]): IerStroka[] {
  let pred = -1;
  return rs.map(x => {
    const g = Math.max(0, Math.min(x.glubina, pred + 1));
    pred = g;
    return g === x.glubina ? x : { ...x, glubina: g };
  });
}
/** выделенные строки вместе с вложенными — блоками [начало, конец) сверху вниз */
function bloki(rs: IerStroka[], vybor: string[]): [number, number][] {
  const idx = vybor.map(k => rs.findIndex(r => r.klyuch === k)).filter(i => i >= 0).sort((a, b) => a - b);
  const out: [number, number][] = [];
  let dalee = -1;
  idx.forEach(i => { if (i < dalee) return; const z = konecPoddereva(rs, i); out.push([i, z]); dalee = z; });
  return out;
}
let schetchik = 1;
const novayaStroka = (name: string, glubina: number): IerStroka => ({ klyuch: 'rb' + (schetchik++), id: null, name, glubina,
  zadach: 0, work_id: null, netronuta: true, raskatka: null, dlit: null, logika: null, imyaSohr: '', kontur: null });

const OTS = 10;   // отступ Ганта слева, px

/** типы связи (эскиз: TIPY): подпись словами и что значит */
const TIPY: [string, string, string][] = [
  ['ОН', 'окончание → начало', 'начинается после окончания предшественника'],
  ['НН', 'начало → начало', 'начинается вместе с предшественником (или со сдвижкой)'],
  ['ОО', 'окончание → окончание', 'заканчивается вместе с предшественником'],
  ['НО', 'начало → окончание', 'заканчивается не раньше начала предшественника'],
];
type SvPole = 'tip' | 'zapas' | 'oper' | 'soft';
/** целое из поля: пусто — 0, не число — null (вернуть прежнее) */
function celoe(t: string): number | null {
  const v = t.trim().replace(',', '.').replace('−', '-');
  if (v === '' || v === '-' || v === '+') return 0;
  const n = Math.round(+v);
  return isNaN(n) ? null : n;
}

/** Окно настройки связи у тега (эскиз: oknoSv). Живёт, пока открыто: правки не пересоздают
    его, курсор в поле остаётся на месте. Тип и галочка сохраняются сразу, числа — через
    0,5 с после набора или при уходе из поля; при закрытии несохранённое число дописывается.
    sbros меняется, когда сервер отказал и значение вернули, — поля перечитываются из связи. */
/** Поиск работы для новой связи: поле + список, фильтр по номеру строки или части названия;
    ↑↓ — выбор, Enter — добавить, Esc / щелчок мимо — закрыть */
function PoiskRaboty({ varianty, placeholder, onVybor, onZakryt }: {
  varianty: { w: string; n: number | string }[]; placeholder: string; onVybor: (w: string) => void; onZakryt: () => void }) {
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const t = q.trim().toLowerCase();
  const spisok = (t ? varianty.filter(v => String(v.n) === t || String(v.n).startsWith(t) || v.w.toLowerCase().includes(t)) : varianty).slice(0, 60);
  useEffect(() => {
    const mimo = (ev: MouseEvent) => { if (ref.current && !ref.current.contains(ev.target as Node)) onZakryt(); };
    const tm = window.setTimeout(() => document.addEventListener('mousedown', mimo), 0);
    return () => { window.clearTimeout(tm); document.removeEventListener('mousedown', mimo); };
  }, [onZakryt]);
  return (
    <div className="rb-poisk" ref={ref} onMouseDown={ev => ev.stopPropagation()}>
      <input autoFocus value={q} placeholder={placeholder} spellCheck={false}
        onChange={ev => { setQ(ev.target.value); setI(0); }}
        onKeyDown={ev => {
          ev.stopPropagation();
          if (ev.key === 'Escape') { ev.preventDefault(); onZakryt(); }
          else if (ev.key === 'ArrowDown') { ev.preventDefault(); setI(x => Math.min(spisok.length - 1, x + 1)); }
          else if (ev.key === 'ArrowUp') { ev.preventDefault(); setI(x => Math.max(0, x - 1)); }
          else if (ev.key === 'Enter') { ev.preventDefault(); const v = spisok[i]; if (v) onVybor(v.w); }
        }} />
      <div className="rb-poisk-sp">
        {spisok.length ? spisok.map((v, k) => (
          <div key={v.w} className={'rb-poisk-v' + (k === i ? ' on' : '')} onMouseEnter={() => setI(k)} onMouseDown={ev => { ev.preventDefault(); onVybor(v.w); }}>
            <i>{v.n}</i>{v.w}</div>))
          : <div className="rb-poisk-net">нет подходящих работ (без самой работы, уже стоящих и тех, что дали бы круг)</div>}
      </div>
    </div>);
}

function OknoSvyazi({ e, at, can, sbros, onPole, onUbrat, onKarta, onZakryt, posle }: { posle?: boolean;
  e: Svyaz; at: { left: number; top: number; bottom: number }; can: boolean; sbros: number;
  onPole: (pole: SvPole, v: string | number | boolean) => void;
  onUbrat: () => void; onKarta: (imya: string) => void; onZakryt: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [zap, setZap] = useState(String(e.zapas));
  const [et, setEt] = useState(String(e.oper));
  const [poz, setPoz] = useState({ left: at.left, top: at.bottom + 6 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight;
    let top = at.bottom + 6;
    if (top + h > window.innerHeight - 8) top = Math.max(8, at.top - h - 6);
    setPoz({ left: Math.max(8, Math.min(at.left, window.innerWidth - w - 8)), top });
  }, [at]);
  useEffect(() => { setZap(String(e.zapas)); setEt(String(e.oper)); }, [sbros]);   // eslint-disable-line react-hooks/exhaustive-deps
  // отложенное сохранение чисел; последнее — в ссылке, чтобы дописать при закрытии
  const zhdet = useRef<{ pole: 'zapas' | 'oper'; t: string } | null>(null);
  const tajmer = useRef<number | null>(null);
  const svezh = useRef({ e, onPole });
  svezh.current = { e, onPole };
  const sohr = () => {
    if (tajmer.current) { window.clearTimeout(tajmer.current); tajmer.current = null; }
    const z = zhdet.current;
    zhdet.current = null;
    if (!z) return;
    const n = celoe(z.t);
    const { e: ee, onPole: op } = svezh.current;
    const bylo = z.pole === 'zapas' ? ee.zapas : ee.oper;
    if (n == null) { if (z.pole === 'zapas') setZap(String(bylo)); else setEt(String(bylo)); return; }
    if (n !== bylo) op(z.pole, n);
  };
  const otlozhit = (pole: 'zapas' | 'oper', t: string) => {
    zhdet.current = { pole, t };
    if (tajmer.current) window.clearTimeout(tajmer.current);
    tajmer.current = window.setTimeout(sohr, 500);
  };
  useEffect(() => () => { sohr(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const tekTip = TIPY.find(x => x[0] === e.tip) || TIPY[0];
  const drug = posle ? e.work : e.pred;   // работа на теге: предшественник или последователь
  const mozhno = can && e.id > 0;
  const klav = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') { ev.preventDefault(); sohr(); }
    if (ev.key === 'Escape') { ev.preventDefault(); onZakryt(); }
  };
  return (
    <div className="rb-svpop" ref={ref} style={{ left: poz.left, top: poz.top }} role="dialog">
      <div className="rb-sv-zag">
        {/* только работа с тега (владелец 2026-10-16: «не пиши „ФП идёт после…“, только название») */}
        <a onClick={() => onKarta(drug)} title="Карточка работы">{drug}</a>
        <span className="rb-sv-x" title="Закрыть (Esc)" onClick={onZakryt}>✕</span>
      </div>
      <div className="rb-sv-ryad"><span className="rb-sv-l">Тип связи</span>
        <span className="rb-seg rb-sv-tip">
          {TIPY.map(t => <button key={t[0]} className={e.tip === t[0] ? 'on' : ''} disabled={!mozhno} title={t[1] + ': ' + t[2]}
            onClick={() => { if (e.tip !== t[0]) onPole('tip', t[0]); }}>{t[0]}</button>)}
        </span>
      </div>
      <div className="rb-sv-opis">{tekTip[1]} — {tekTip[2]}</div>
      <div className="rb-sv-ryad"><label className="rb-sv-l" htmlFor="rbSvZap">Сдвижка начала</label>
        <input id="rbSvZap" value={zap} disabled={!mozhno} inputMode="numeric" onKeyDown={klav} onFocus={ev => ev.target.select()}
          onChange={ev => { setZap(ev.target.value); otlozhit('zapas', ev.target.value); }} onBlur={sohr} />
        <span className="rb-sv-ed">дн</span><span className="rb-sv-pod">плюс — задержка, минус — нахлёст</span></div>
      <div className="rb-sv-ryad"><label className="rb-sv-l" htmlFor="rbSvEt">Сдвижка по этажам</label>
        <input id="rbSvEt" value={et} disabled={!mozhno} inputMode="numeric" onKeyDown={klav} onFocus={ev => ev.target.select()}
          onChange={ev => { setEt(ev.target.value); otlozhit('oper', ev.target.value); }} onBlur={sohr} />
        <span className="rb-sv-ed">эт.</span><span className="rb-sv-pod">плюс — предшественник уходит вперёд на столько этажей</span></div>
      <label className="rb-sv-ryad rb-sv-myag">
        <input type="checkbox" checked={e.soft} disabled={!mozhno} onChange={ev => onPole('soft', ev.target.checked)} />
        Мягкая (только порядок, дату не держит)
      </label>
      {mozhno
        ? <div className="rb-sv-niz"><button className="rb-sv-ubr" onClick={onUbrat}>Убрать связь</button>
            <button className="rb-sv-ok" onClick={() => { sohr(); onZakryt(); }}>Готово</button></div>
        : e.id < 0 && can ? <div className="rb-sv-opis">связь ещё сохраняется…</div> : null}
    </div>
  );
}

export function RabotyEkran({ ctx }: { ctx: ShellCtx }) {
  const [sprOkno, setSprOkno] = useState<Anchor | null>(null);   // «Справочники» (владелец 2026-10-17)
  const ekran = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const tw = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  // ---- разбивки проекта и выбор группировки (шаг 5): своя разбивка `rz:<id>` или признак ----
  const [razbivki, setRazbivki] = useState<Razbivka[]>([]);
  const [grup, setGrupS] = useState<GrupK>(() => {
    try { return localStorage.getItem(GRUP_LS) || 'etapy'; } catch { return 'etapy'; }
  });
  // запоздалый ответ прежнего проекта не должен подменить список: считаем запросы, применяем только последний
  const rzSeq = useRef(0);
  const zagruzitRazbivki = useCallback(async () => {
    const seq = ++rzSeq.current;
    const r = await call<Razbivka[]>('get', p('/api/projects/{pid}/razbivki', { pid: ctx.pid }));
    if (seq === rzSeq.current && r.ok) setRazbivki(r.data);
  }, [ctx.pid]);
  useEffect(() => { setRazbivki([]); }, [ctx.pid]);   // сменили проект — список прежнего не показываем
  useEffect(() => { void zagruzitRazbivki(); }, [zagruzitRazbivki, ctx.reloadToken]);
  const osn = razbivki.find(r => r.osnovnaya) || null;
  const osnName = osn?.name || 'Этапы';
  /** открытая своя разбивка; «etapy» (старая память) и неизвестный id — основная */
  const tekRazb = useMemo((): Razbivka | null => {
    if (!grup.startsWith(RZ) && grup !== 'etapy') return null;
    const id = +grup.slice(RZ.length);
    return razbivki.find(r => r.id === id) || osn || (razbivki.length ? null : { id: 0, name: 'Этапы', osnovnaya: true, poryadok: 0, rev: 0 });
  }, [grup, razbivki, osn]);
  const svoya = !!tekRazb;
  // хук работает с основной разбивкой без параметра (как старый фронт); своя — по id
  const tekRid = tekRazb && !tekRazb.osnovnaya ? tekRazb.id : null;
  const ie = useIerarhiya(ctx, ekran, true, tekRid);
  const can = ie.can;
  const R = useMemo(() => nastoyashchie(ie.rows), [ie.rows]);
  const deti = useMemo(() => R.map((_, i) => estDeti(R, i)), [R]);

  // ---- выделение, правка названия и «Дней», черновик новой работы ----
  const [vyborSyr, setVybor] = useState<string[]>([]);   // ключи строк в порядке выделения
  const vybor = useMemo(() => vyborSyr.filter(k => R.some(r => r.klyuch === k)), [vyborSyr, R]);
  const yakor = useRef<string | null>(null);
  const [pravim, setPravim] = useState<string | null>(null);
  /** Enter на выбранной работе (владелец 2026-10-16): строка для новой работы сразу под ней, в том же
      разделе; в группировке по признаку новая работа получает значение группы (после сохранения) */
  const [vst, setVst] = useState<{ posle: string; val: string | null } | null>(null);
  const zhdyotPriznak = useRef(new Map<string, { patch: string; val: string | null; ushlo?: boolean }>());
  // подработа из «Разбить» (2026-10-26): id работы → строка ГПР, пока свойства работ не перечитаны — структура
  // приходит раньше, и без этого новая строка мелькала в «Отдельных работах» (замечание владельца)
  const podrZhdut = useRef(new Map<number, string>());
  const otmenaImeni = useRef(false);
  const [dnAkt, setDnAkt] = useState<string | null>(null);
  const [dnPravka, setDnPravka] = useState<{ k: string; start: string } | null>(null);
  const dnZakryto = useRef(false);   // поле «Дней» уже сохранено по Enter — потеря фокуса не сохраняет второй раз
  const [chern, setChern] = useState(false);
  const [spisokDlya, setSpisokDlya] = useState<string | null>(null);   // у какого «＋» сейчас строится список работ
  const [raskryto, setRaskryto] = useState<Set<string>>(new Set());   // ячейки связей, развёрнутые из «＋N ещё»
  const chernInp = useRef<HTMLInputElement>(null);
  const [tost, setTost] = useState('');
  const tostT = useRef<number | null>(null);
  const toast = useCallback((m: string) => {
    setTost(m);
    if (tostT.current) window.clearTimeout(tostT.current);
    tostT.current = window.setTimeout(() => setTost(''), 2600);
  }, []);
  useEffect(() => () => { if (tostT.current) window.clearTimeout(tostT.current); }, []);

  // ---- связи: строки всех шаблонов проекта (с id — чтобы удалять) ----
  const [sv, setSv] = useState<Svyaz[]>([]);
  const [tid, setTid] = useState(0);
  const [svBusy, setSvBusy] = useState(0);
  const otmena = useRef<{ ids: number[]; vybor: string[] } | null>(null);   // последнее «Связать» — для Ctrl+Z
  const zagruzitSv = useCallback(async () => {
    const t = await call<Tpl[]>('get', '/api/link-templates?project=' + ctx.pid);
    const tpls = t.ok ? t.data : [];
    const vse = await Promise.all(tpls.map(async x => {
      const r = await call<TplRow[]>('get', p('/api/link-templates/{tid}/rows', { tid: x.id }));
      return r.ok ? r.data : [];
    }));
    const byla = new Set<string>();
    const out: Svyaz[] = [];
    vse.flat().forEach(r => {
      const kl = r.pred + '\u0001' + r.work;
      if (byla.has(kl)) return;   // одна и та же пара в двух шаблонах — одна связь
      byla.add(kl);
      out.push({ id: r.id, work: r.work, pred: r.pred, tip: r.tip, zapas: +r.zapas || 0, oper: +r.operezhenie || 0, soft: !!r.soft });
    });
    setSv(out);
    const glavnyj = tpls.slice().sort((x, y) => y.svyazey - x.svyazey).find(x => x.svyazey > 0)
      || tpls.find(x => x.name.toLowerCase() === 'базовый');
    setTid(glavnyj?.id || 0);
  }, [ctx.pid]);
  useEffect(() => { void zagruzitSv(); }, [zagruzitSv, ctx.reloadToken]);

  // ---- производные: номера, работы по имени, видимые строки ----
  const poImeni = useMemo(() => {
    const m = new Map<string, number>();
    R.forEach((r, i) => { if (!deti[i]) m.set(imyaSv(r), i); });
    return m;
  }, [R, deti]);
  const svyazi = useMemo(() => sv.filter(e => poImeni.has(e.work) && poImeni.has(e.pred)), [sv, poImeni]);
  const vidimye = useMemo(() => {
    const out: number[] = [];
    let skryt = Infinity;
    R.forEach((r, i) => {
      if (r.glubina > skryt) return;
      skryt = Infinity;
      out.push(i);
      if (deti[i] && ie.svernuto.has(r.klyuch)) skryt = r.glubina;
    });
    return out;
  }, [R, deti, ie.svernuto]);
  // ---- группировка: своя разбивка (иерархия) или по признаку работы ----
  const gDef: typeof GRUPPY[number] | typeof SVOYA = svoya ? SVOYA : (GRUPPY.find(g => g.k === grup) || SVOYA);
  const [wk, setWk] = useState<Map<number, Rabota>>(new Map());   // работы справочника — их признаки
  // строки ГПР проекта (#119): ручная длительность и id для правки — по названию строки
  const [strokiGpr, setStrokiGpr] = useState<Map<string, { id: number; dlit: number | null }>>(new Map());
  const [dnStr, setDnStr] = useState<string | null>(null);   // у какой строки ГПР правится длительность
  // строка ввода подработ после «Разбить»: stroka — куда встают, obrazec — работа, чьи признаки получают
  const [podr, setPodr] = useState<{ stroka: string; obrazec: number } | null>(null);
  const [spiski, setSpiski] = useState<{ kod: string; znachenie: string }[]>([]);
  const [svGr, setSvGr] = useState<Set<string>>(new Set());       // свёрнутые группы
  const wkSeq = useRef(0);
  const zagruzitRaboty = useCallback(async () => {
    const seq = ++wkSeq.current;
    const [w, sp, sg] = await Promise.all([
      call<Rabota[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<{ kod: string; znachenie: string }[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
      call<{ id: number; name: string; dlit_ruchnaya: number | null }[]>('get', p('/api/projects/{pid}/stroki-gpr', { pid: ctx.pid })),
    ]);
    if (seq !== wkSeq.current) return;   // ответ прежнего проекта
    if (w.ok) setWk(new Map(w.data.map(x => [x.id, x])));
    if (sp.ok) setSpiski(sp.data);
    if (sg.ok) setStrokiGpr(new Map(sg.data.map(x => [x.name, { id: x.id, dlit: x.dlit_ruchnaya }])));
  }, [ctx.pid]);
  useEffect(() => { void zagruzitRaboty(); }, [zagruzitRaboty, ctx.reloadToken]);
  function setGrup(v: GrupK) {
    setGrupS(v);
    setVybor([]);
    yakor.current = null;
    setDnAkt(null);
    setChern(false);
    try { localStorage.setItem(GRUP_LS, v); } catch { /* приватный режим */ }
  }
  // ---- свои разбивки: создать, переименовать, удалить (эскиз: pereklyuchatel, «＋ своя разбивка») ----
  const [novRazb, setNovRazb] = useState(false);
  const novRazbInp = useRef<HTMLInputElement>(null);
  useEffect(() => { if (novRazb) novRazbInp.current?.focus(); }, [novRazb]);
  async function sozdatRazbivku(name: string) {
    const nm = name.trim();
    if (!nm) return;
    const r = await call<Data<'/api/projects/{pid}/razbivki', 'post'>>('post', p('/api/projects/{pid}/razbivki', { pid: ctx.pid }), { name: nm });
    if (!r.ok) { toast(r.error.message); return; }
    setNovRazb(false);
    setRazbivki(cur => [...cur, r.data]);
    setGrup(RZ + r.data.id);
    toast('Разбивка «' + nm + '» добавлена: пока все работы списком (' + r.data.rabot + ') — заведите заголовки «＋ Этап» и вложите работы «Вправо» или перетаскиванием');
  }
  async function pereimenovatRazbivku(rz: Razbivka) {
    if (!can || rz.osnovnaya) return;
    const nm = await ask('Название разбивки:', rz.name);
    if (!nm || nm === rz.name) return;
    const r = await call<Razbivka>('patch', p('/api/razbivki/{rid}', { rid: rz.id }), { name: nm });
    if (!r.ok) { toast(r.error.message); return; }
    setRazbivki(cur => cur.map(x => (x.id === rz.id ? r.data : x)));
    toast('Разбивка переименована: «' + nm + '»');
  }
  async function udalitRazbivku(rz: Razbivka) {
    if (!can || rz.osnovnaya) return;
    const ok = await confirmBox('Удалить разбивку «' + rz.name + '»? Уйдут только её заголовки и порядок строк — работы останутся в графике и в остальных разбивках.', { ok: 'Удалить', opasno: true });
    if (!ok) return;
    const r = await call('delete', p('/api/razbivki/{rid}', { rid: rz.id }));
    if (!r.ok) { toast(r.error.message); return; }
    setRazbivki(cur => cur.filter(x => x.id !== rz.id));
    setGrup(osn ? RZ + osn.id : 'etapy');
    toast('Разбивка «' + rz.name + '» удалена, работы на месте');
  }
  /** значения признака из «Списков» — в их порядке; написание работы сверяется без учёта регистра */
  const spisokZnach = useMemo(() => ('kod' in gDef ? spiski.filter(x => x.kod === gDef.kod).map(x => x.znachenie.trim()).filter(Boolean) : []), [spiski, gDef]);
  const znachRaboty = (r: IerStroka): string | null => {
    if (!('pole' in gDef)) return null;
    const zh = zhdyotPriznak.current.get(r.klyuch);
    if (zh && zh.patch === gDef.patch) return zh.val;
    if (!r.work_id) return null;
    const w = wk.get(r.work_id);
    if (!w && gDef.k === 'stroka' && podrZhdut.current.has(r.work_id)) return podrZhdut.current.get(r.work_id)!;
    const v = String((w ? w[gDef.pole] : null) ?? '').trim();
    if (!v) return null;
    return spisokZnach.find(z => z.toLowerCase() === v.toLowerCase()) || v;
  };
  const gruppy = useMemo((): Gruppa[] => {
    if (!('pole' in gDef)) return [];
    const rab = R.map((_, i) => i).filter(i => !deti[i]);
    const po = new Map<string | null, number[]>();
    rab.forEach(i => { const v = znachRaboty(R[i]); const a = po.get(v); if (a) a.push(i); else po.set(v, [i]); });
    // «Строка в ГПР»: строка из одной работы — обычная работа, не суммарная (владелец 2026-10-26) — в «Отдельных
    // работах»; кроме строки, которую сейчас разбивают (под ней строка ввода подработ)
    if (gDef.k === 'stroka') {
      [...po.entries()].forEach(([v, idx]) => {
        if (v == null || idx.length > 1 || v === podr?.stroka) return;
        po.delete(v);
        po.set(null, [...(po.get(null) || []), ...idx].sort((a, b) => a - b));
      });
    }
    const dop = [...po.keys()].filter((v): v is string => v != null && !spisokZnach.includes(v)).sort((a, b) => a.localeCompare(b, 'ru'));
    const out: Gruppa[] = [...new Set([...spisokZnach, ...dop])].map(v => ({ k: 'grp:' + v, val: v, name: v, idx: po.get(v) || [] }))
      .filter(g => g.idx.length);   // пустые группы не показываем (владелец 2026-10-16)
    if (po.get(null)?.length) out.push({ k: 'grp:', val: null, idx: po.get(null)!,
      name: gDef.k === 'stroka' ? 'Отдельные работы' : '— не задано —' });   // каждая — сама себе строка ГПР (пояснение внизу экрана)
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [R, deti, wk, gDef, spisokZnach, podr]);
  /** что на экране и в каком порядке: в группировке — заголовки групп и работы в порядке иерархии */
  // ---- поиск работы (владелец 2026-10-16): «стояки» — видны только подходящие работы, их разделы и группы ----
  const [poisk, setPoisk] = useState('');
  const [poiskSp, setPoiskSp] = useState<number | null>(null);   // список под полем: null — закрыт, число — подсвеченный пункт (-1 — «выделить все»)
  /** отмеченные галочками в списке работы (несколько, владелец 2026-10-16): если есть — в таблице только они */
  const [otbor, setOtbor] = useState<Set<string>>(new Set());
  const poTekstu = useMemo(() => {
    const q = poisk.trim().toLowerCase();
    if (!q) return null;
    return R.map((_, i) => i).filter(i => !deti[i] && R[i].name.toLowerCase().includes(q));
  }, [poisk, R, deti]);
  const naideno = useMemo(() => {
    if (otbor.size) return new Set(R.map((_, i) => i).filter(i => !deti[i] && otbor.has(R[i].klyuch)));
    return poTekstu ? new Set(poTekstu) : null;
  }, [otbor, poTekstu, R, deti]);
  /** работы для списка под полем поиска: найденные или все, по порядку строк */
  const spisokPoiska = useMemo(() => (poTekstu || R.map((_, i) => i).filter(i => !deti[i])).slice(0, 300), [R, deti, poTekstu]);
  /** отметить набор работ: в таблице — только они, и они выделены (признак — сразу всем) */
  function primenitOtbor(nov: Set<string>) {
    setOtbor(nov);
    const ks = R.filter(r => nov.has(r.klyuch)).map(r => r.klyuch);
    setVybor(ks); yakor.current = ks[0] ?? null;
  }
  function vydelitNaidennye() {
    const ks = (poTekstu || []).map(i => R[i].klyuch);
    if (!ks.length) return;
    const vse = ks.every(k => otbor.has(k));
    const nov = new Set(otbor);
    ks.forEach(k => (vse ? nov.delete(k) : nov.add(k)));
    primenitOtbor(nov);
  }
  function vybratIzPoiska(i: number) {
    const k = R[i].klyuch;
    const nov = new Set(otbor);
    if (nov.has(k)) nov.delete(k); else nov.add(k);
    primenitOtbor(nov);
  }
  function sbrositPoisk() { setPoisk(''); setOtbor(new Set()); }
  const pokaz = useMemo((): Pokaz[] => {
    if (svoya) {
      if (!naideno) return vidimye.map(i => ({ gr: null, i }));
      // при поиске разделы раскрыты: раздел виден, если в нём есть найденная работа
      const out: Pokaz[] = [];
      R.forEach((_, i) => {
        if (deti[i]) { const z = konecPoddereva(R, i); for (let j = i + 1; j < z; j++) if (naideno.has(j)) { out.push({ gr: null, i }); break; } }
        else if (naideno.has(i)) out.push({ gr: null, i });
      });
      return out;
    }
    if (grup === 'net') return R.map((_, i) => i).filter(i => !deti[i] && (!naideno || naideno.has(i))).map(i => ({ gr: null, i }));
    const out: Pokaz[] = [];
    gruppy.forEach(g => {
      const idx = naideno ? g.idx.filter(i => naideno.has(i)) : g.idx;
      if (!idx.length) return;
      const g2 = idx === g.idx ? g : { ...g, idx };
      // «Строка в ГПР»: работа с названием строки (разбитая кнопкой «Разбить») — сама заголовок: жирная, без
      // отступа, со своими днями и связями; подработы — под ней (владелец 2026-10-26)
      const nm = (g.val || '').trim().toLowerCase();
      const glav = grup === 'stroka' && nm ? idx.find(i => R[i].name.trim().toLowerCase() === nm) : undefined;
      if (glav != null) {
        out.push({ gr: null, i: glav, glav: g2 });
        if (!svGr.has(g.k) || naideno) idx.forEach(i => { if (i !== glav) out.push({ gr: null, i }); });
        return;
      }
      out.push({ gr: g2, i: -1 });
      if (!svGr.has(g.k) || naideno) idx.forEach(i => out.push({ gr: null, i }));
    });
    return out;
  }, [svoya, grup, vidimye, R, deti, gruppy, svGr, naideno]);
  const pokazIdx = useMemo(() => pokaz.filter(x => !x.gr).map(x => x.i), [pokaz]);
  // ---- вид: «Связи и график» или «Параметры» (не зависит от группировки) ----
  const [vid, setVidS] = useState<Vid>(() => {
    try { const v = localStorage.getItem(VID_LS); return v === 'param' || v === 'gpr' ? 'gpr' : v === 'otch' ? 'otch' : 'svyazi'; } catch { return 'svyazi'; }
  });
  const param = vid !== 'svyazi';
  // ширина столбца «Работа» — тянется за правый край заголовка, помнится в браузере (владелец 2026-10-16)
  const NM_W = 320;   // 240 → 320 (2026-10-17): названия реже переносятся, строки ниже — как в «Планировании»
  const [nmW, setNmW] = useState<number>(() => { try { const v = +(localStorage.getItem('rb_nm_w') || ''); return v >= 160 && v <= 700 ? v : NM_W; } catch { return NM_W; } });
  useEffect(() => { try { localStorage.setItem('rb_nm_w', String(nmW)); } catch { /* приватный режим */ } }, [nmW]);
  function nmTyanut(ev: React.PointerEvent) {
    ev.preventDefault(); ev.stopPropagation();
    const x0 = ev.clientX, w0 = nmW;
    document.body.classList.add('rb-shirina');
    const dv = (e: PointerEvent) => setNmW(Math.max(160, Math.min(700, Math.round(w0 + e.clientX - x0))));
    const up = () => { window.removeEventListener('pointermove', dv); window.removeEventListener('pointerup', up); document.body.classList.remove('rb-shirina'); };
    window.addEventListener('pointermove', dv);
    window.addEventListener('pointerup', up);
  }
  /** столбцы открытого вида: «ГПР» — расчётные, «Отчёты» — признаки и разбивки */
  const polyaVida = POLYA.filter(pl => pl.vid === vid);
  function setVid(v: Vid) {
    setVidS(v);
    setOkno(null);
    try { localStorage.setItem(VID_LS, v); } catch { /* приватный режим */ }
  }
  /** значение поля работы для списка: признаки — в написании «Списков», раскатка — ключ */
  function znachPolya(r: IerStroka, pole: PoleK): string {
    const w = r.work_id ? wk.get(r.work_id) : undefined;
    if (pole === 'raskatka') return (w?.raskatka || r.raskatka || 'этажи');
    if (pole === 'kalendar') return w?.kalendar || 'календарные';
    if (pole === 'korpusa') return String(w?.korpusa ?? '').trim();
    if (pole === 'stroka_gpr') {
      const v = String(w?.stroka_gpr ?? (r.work_id ? podrZhdut.current.get(r.work_id) : '') ?? '').trim();
      // строка из одной работы — обычная работа (владелец 2026-10-26)
      return v && [...wk.values()].filter(x => x.stroka_gpr === v).length > 1 ? v : '';
    }
    if (pole === 'logika') return String((w ? w.logika : r.logika) ?? '').trim();
    const v = String((w ? w[pole] : null) ?? '').trim();
    const kod = pole === 'sistema' ? 'gruppa' : pole;   // код списка в «Списках»
    const sp = spiski.filter(x => x.kod === kod).map(x => x.znachenie.trim());
    return (v && sp.find(z => z.toLowerCase() === v.toLowerCase())) || v;
  }
  /** варианты списка: «Списки» + встретившиеся у работ (признаки), логики проекта (порядок) */
  const variantyPolya = useMemo(() => {
    const m = new Map<PoleK, string[]>();
    m.set('raskatka', Object.keys(RASK_LABEL));
    m.set('logika', ie.logiki);
    m.set('kalendar', Object.keys(KAL_LABEL));
    m.set('stroka_gpr', [...new Set([...wk.values()].map(w => String(w.stroka_gpr ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ru')));
    POLYA.forEach(pl => {
      if (!('kod' in pl)) return;
      const sp = spiski.filter(x => x.kod === pl.kod).map(x => x.znachenie.trim()).filter(Boolean);
      const est = [...wk.values()].map(w => String(w[pl.k as 'sistema' | 'mesto' | 'kontur'] ?? '').trim())
        .filter(v => v && !sp.some(z => z.toLowerCase() === v.toLowerCase()));
      m.set(pl.k, [...new Set([...sp, ...est.sort((a, b) => a.localeCompare(b, 'ru'))])]);
    });
    return m;
  }, [spiski, wk, ie.logiki]);
  /** «＋ новое значение…» в списке признака: спросить название, добавить его в «Списки» проекта
      (уже есть — не страшно) и назначить работе или всем выделенным (2026-10-16) */
  async function novoeZnachenie(r: IerStroka, pole: PoleK) {
    const pl = POLYA.find(x => x.k === pole)!;
    if (pole === 'stroka_gpr') {   // строку ГПР заводит сервер по названию — в «Списки» не пишется
      const vvod = await ask('Новая строка ГПР — название:', '');
      const zn = (vvod || '').trim();
      if (zn) await smenitPole(r, pole, zn);
      return;
    }
    if (!('kod' in pl)) return;
    const vvod = await ask('Новое значение «' + pl.t + '»:', '');
    const zn = (vvod || '').trim();
    if (!zn) return;
    const est = (variantyPolya.get(pole) || []).find(z => z.toLowerCase() === zn.toLowerCase());
    if (!spiski.some(x => x.kod === pl.kod && x.znachenie.trim().toLowerCase() === zn.toLowerCase())) {
      const res = await call('post', p('/api/projects/{pid}/spiski', { pid: ctx.pid }), { kod: pl.kod, znachenie: est || zn });
      if (!res.ok && res.error.code !== 'dup') { ctx.onError?.(res.error.message); return; }
    }
    await smenitPole(r, pole, est || zn);
  }
  /** правка поля в «Параметрах»: строка среди выделенных (2+) — всем выделенным; запросы по очереди */
  async function smenitPole(r: IerStroka, pole: PoleK, v: string) {
    if (!can) return;
    const pl = POLYA.find(x => x.k === pole)!;
    const komu = (vybor.includes(r.klyuch) && vybRaboty.length > 1 ? vybRaboty : [r]).filter(w => w.work_id && znachPolya(w, pole) !== v);
    if (!komu.length) return;
    const zn = v === '' ? null : v;
    setSvBusy(b => b + 1);
    let n = 0, plus = 0, minus = 0, vnutri = 0;
    const oshibki: string[] = [];
    try {
      for (const w of komu) {
        const res = await call<{ sozdano_zadach?: number; udaleno_zadach?: number; svyazej_vnutri?: number }>('patch', p('/api/works/{wid}', { wid: w.work_id! }), { [pl.patch]: zn });
        if (!res.ok) { oshibki.push('«' + w.name + '»: ' + res.error.message); continue; }
        vnutri += res.data?.svyazej_vnutri || 0;
        n++;
        plus += res.data?.sozdano_zadach || 0;
        minus += res.data?.udaleno_zadach || 0;
      }
    } finally {
      await zagruzitRaboty();
      setSvBusy(b => b - 1);
    }
    const pod = pole === 'raskatka' ? (RASK_LABEL[v] || v) : pole === 'kalendar' ? (KAL_LABEL[v] || v) : pole === 'korpusa' ? (zn ?? 'все')
      : pole === 'stroka_gpr' ? (zn ?? 'сама себе строка') : zn ?? 'не задано';
    const zad = plus || minus ? ' · задач +' + plus + (minus ? ' / убрано ' + minus : '') : '';
    if (oshibki.length) { ctx.onError?.(oshibki[0]); }
    toast((n > 1 ? 'Применено к ' + n + ' работам: ' : n ? '«' + komu[0].name + '»: ' : '') + (n ? pl.t.toLowerCase() + ' — ' + pod + zad : '')
      + (vnutri ? ' · внутри строки ' + vnutri + ' связ' + (vnutri === 1 ? 'ь' : vnutri < 5 ? 'и' : 'ей') + ' — они больше не влияют на сроки' : '')
      + (oshibki.length ? (n ? ' · ' : '') + 'не сохранилось: ' + oshibki.join('; ') : ''));
    if (n) ctx.onChanged?.();
  }

  // ---- столбцы разбивок в «Параметрах» (шаг 5; эскиз: RZcell / razdelyRazb / razdelV): раздел, где стоит
  // работа в каждой разбивке; выбор переносит её в конец раздела той разбивки. Открытая разбивка — по
  // строкам хука (живые), остальные — деревья с сервера (перечитываются после каждого сохранения)
  const [derevya, setDerevya] = useState<Map<number, { uzly: Uzel[]; rev: number }>>(new Map());
  const zagruzitDerevya = useCallback(async (krome: number | null) => {
    const vse = await Promise.all(razbivki.filter(rz => rz.id !== krome).map(async rz => {
      const r = await call<Data<'/api/projects/{pid}/wbs', 'get'>>('get', p('/api/projects/{pid}/wbs', { pid: ctx.pid }) + '?razbivka=' + rz.id);
      return [rz.id, r.ok ? { uzly: r.data.uzly, rev: r.data.rev } : null] as const;
    }));
    setDerevya(new Map(vse.filter((x): x is readonly [number, { uzly: Uzel[]; rev: number }] => !!x[1])));
  }, [razbivki, ctx.pid]);
  const tekRzId = tekRazb ? tekRazb.id : (osn?.id ?? null);
  useEffect(() => {
    if (vid !== 'otch' || !razbivki.length) return;
    void zagruzitDerevya(tekRzId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid, zagruzitDerevya, tekRzId, ie.rev, ctx.reloadToken]);
  /** разделы дерева с путём «Раздел / Подраздел» (ключ — id узла) */
  const razdelyDereva = (uzly: Uzel[]): Razdel[] => {
    const put: string[] = [];
    const out: Razdel[] = [];
    uzly.forEach(u => { put.length = u.glubina; put.push(u.name); if (u.razdel) out.push({ k: String(u.id), put: put.join(' / ') }); });
    return out;
  };
  /** разделы открытой разбивки — по строкам на экране (ключ — ключ строки) */
  const razdelyTek = useMemo((): Razdel[] => {
    const put: string[] = [];
    const out: Razdel[] = [];
    R.forEach((r, i) => { put.length = r.glubina; put.push(r.name); if (deti[i]) out.push({ k: r.klyuch, put: put.join(' / ') }); });
    return out;
  }, [R, deti]);
  const razdelyRz = (rz: Razbivka): Razdel[] => (rz.id === tekRzId ? razdelyTek : razdelyDereva(derevya.get(rz.id)?.uzly || []));
  /** ключ раздела, в котором работа стоит в разбивке ('' — без раздела, null — разбивка не загружена) */
  const razdelRaboty = (rz: Razbivka, r: IerStroka): string | null => {
    if (rz.id === tekRzId) {
      const i = R.indexOf(r);
      const j = roditel(R, i);
      return j < 0 ? '' : R[j].klyuch;
    }
    const d = derevya.get(rz.id);
    if (!d) return null;
    const u = d.uzly.find(x => x.work_id != null && x.work_id === r.work_id);
    return !u || u.parent_id == null ? '' : String(u.parent_id);
  };
  /** перенести работу (и выделенные с ней) в конец раздела `k` разбивки `rz` ('' — без раздела, в конец) */
  async function perenestiVRazdel(rz: Razbivka, r: IerStroka, k: string) {
    if (!can) return;
    const komu = (vybor.includes(r.klyuch) && vybRaboty.length > 1 ? vybRaboty : [r]).filter(w => w.work_id && razdelRaboty(rz, w) !== k);
    if (!komu.length) return;
    const kuda = k ? razdelyRz(rz).find(x => x.k === k)?.put || '' : 'без этапа';
    const pod = (komu.length > 1 ? komu.length + ' работ' : '«' + komu[0].name + '»') + ' в разбивке «' + rz.name + '»: ' + kuda;
    type Str = { id: number | null; klyuch: string; name: string; glubina: number; work_id: number | null };
    /** общий перенос по плоскому списку с глубинами: убрать строку работы, вставить в конец поддерева раздела */
    const perenos = <T extends Str>(rs: T[], najti: (x: T) => boolean, razdel: (x: T) => boolean, g0 = 0): T[] => {
      let n = rs.slice();
      const a = n.findIndex(najti);
      if (a < 0) return rs;
      const z = konecPoddereva(n, a);
      const kusok = n.slice(a, z);
      n = [...n.slice(0, a), ...n.slice(z)];
      let idx = n.length, g = g0;
      if (k) {
        const s = n.findIndex(razdel);
        if (s < 0) return rs;
        idx = konecPoddereva(n, s);
        g = n[s].glubina + 1;
      }
      const d = g - kusok[0].glubina;
      return [...n.slice(0, idx), ...kusok.map(x => ({ ...x, glubina: x.glubina + d })), ...n.slice(idx)];
    };
    if (rz.id === tekRzId) {
      const ids = komu.map(w => w.klyuch);
      pravka(rs => ids.reduce((n, kl) => perenos(n, x => x.klyuch === kl, x => x.klyuch === k), rs), true);
      toast(pod);
      return;
    }
    const d = derevya.get(rz.id);
    if (!d) return;
    let rs: Str[] = d.uzly.map(u => ({ id: u.id, klyuch: String(u.id), name: u.name, glubina: u.glubina, work_id: u.work_id }));
    komu.forEach(w => {   // работы, у которых в той разбивке ещё нет строки, — в конец, сервер прицепит по имени
      if (!rs.some(x => x.work_id === w.work_id)) rs.push({ id: null, klyuch: 'w' + w.work_id, name: imyaSv(w), glubina: 0, work_id: w.work_id });
    });
    rs = komu.reduce((n, w) => perenos(n, x => x.work_id === w.work_id, x => String(x.id) === k), rs);
    const stroki = rs.map((x, i) => { const j = roditel(rs, i); return { id: x.id, klyuch: x.klyuch, roditel: j < 0 ? null : rs[j].klyuch, name: x.name }; });
    setSvBusy(b => b + 1);
    try {
      const res = await call<Data<'/api/projects/{pid}/wbs', 'put'>>('put', p('/api/projects/{pid}/wbs', { pid: ctx.pid }) + '?razbivka=' + rz.id, { stroki, rev: d.rev });
      if (!res.ok) { toast(res.error.message); ctx.onError?.(res.error.message); await zagruzitDerevya(tekRzId); return; }
      setDerevya(cur => { const n = new Map(cur); n.set(rz.id, { uzly: res.data.uzly, rev: res.data.rev }); return n; });
      toast(pod);
    } finally {
      setSvBusy(b => b - 1);
    }
  }
  /** уровень строки на экране: в своей разбивке — свой, в группе — 1, без группировки — 0 */
  const G = (r: IerStroka) => (svoya ? r.glubina : grup === 'net' ? 0 : 1);
  /** признак работе (и всем выделенным, если тянули одну из них) — PATCH, затем перечитать работы */
  async function smenitPriznak(id: string, val: string | null) {
    if (!('patch' in gDef) || !can) return;
    const r = R[idxK(id)];
    if (!r) return;
    const komu = (vybor.includes(id) && vybRaboty.length > 1 ? vybRaboty : [r]).filter(w => w.work_id && znachRaboty(w) !== val);
    if (!komu.length) return;
    setSvBusy(b => b + 1);
    let n = 0, vnutri = 0;
    try {
      for (const w of komu) {
        const res = await call<{ svyazej_vnutri?: number }>('patch', p('/api/works/{wid}', { wid: w.work_id! }), { [gDef.patch]: val });
        if (!res.ok) { toast(res.error.message); ctx.onError?.(res.error.message); break; }
        vnutri += res.data?.svyazej_vnutri || 0;
        n++;
      }
    } finally {
      await zagruzitRaboty();
      setSvBusy(b => b - 1);
    }
    if (n) {
      toast((n > 1 ? n + ' работ' : komu[0].name) + ': ' + gDef.t.toLowerCase() + ' — ' + (val ?? 'не задано')
        + (vnutri ? ' · внутри строки ' + vnutri + ' связ' + (vnutri === 1 ? 'ь' : vnutri < 5 ? 'и' : 'ей') + ' — они больше не влияют на сроки' : ''));
      ctx.onChanged?.();
    }
  }
  const idxK = (k: string) => R.findIndex(r => r.klyuch === k);
  /** выделенные работы в порядке выделения (разделы не участвуют в связях и «Дней») */
  const vybRaboty = useMemo(() => vybor.map(k => R.findIndex(r => r.klyuch === k)).filter(i => i >= 0 && !deti[i]).map(i => R[i]), [vybor, R, deti]);

  /** все работы, что идут (прямо или через цепочку) после u, — считаются один раз на набор связей */
  const potomki = useMemo(() => {
    const sled = new Map<string, string[]>();
    sv.forEach(e => { const a = sled.get(e.pred); if (a) a.push(e.work); else sled.set(e.pred, [e.work]); });
    const kesh = new Map<string, Set<string>>();
    return (u: string) => {
      let s = kesh.get(u);
      if (s) return s;
      s = new Set<string>();
      const stek = [u];
      while (stek.length) { const x = stek.pop()!; (sled.get(x) || []).forEach(y => { if (!s!.has(y)) { s!.add(y); stek.push(y); } }); }
      kesh.set(u, s);
      return s;
    };
  }, [sv]);
  /** идёт ли pred (прямо или через цепочку) после u — тогда связь pred → u дала бы круг;
      spisok — свой набор связей (цепочка «Связать» вместе с ещё не сохранёнными) */
  function krugIz(u: string, pred: string, spisok?: { work: string; pred: string }[]) {
    if (u === pred) return true;
    if (!spisok) return potomki(u).has(pred);
    const seen = new Set<string>();
    const idti = (x: string): boolean => {
      if (x === pred) return true;
      if (seen.has(x)) return false;
      seen.add(x);
      return spisok.some(e => e.pred === x && idti(e.work));
    };
    return idti(u);
  }

  // ---- Гант одного типового этажа: ранние сроки от предшественников, обратный проход ----
  const rasch = useMemo(() => {
    const dni = (nm: string) => { const i = poImeni.get(nm); return i == null ? 0 : Math.max(0, +(R[i].dlit ?? 0) || 0); };
    // мягкая связь — только порядок этажей, дату не держит никогда (#68) — в расчёт не идёт
    const tverd = svyazi.filter(e => !e.soft);
    const g = new Map<string, Rasch>();
    const v = new Set<string>();
    const sch = (nm: string): Rasch => {
      const est = g.get(nm);
      if (est) return est;
      if (v.has(nm)) return { es: 0, ef: 0, ved: null, lf: 0, zapas: 0, krit: false };
      v.add(nm);
      const d = dni(nm);
      let luchshee = -Infinity, ved: string | null = null;   // ved — предшественник, который держит начало
      tverd.filter(e => e.work === nm).forEach(e => {
        const pg = sch(e.pred);
        const sdv = e.zapas + e.oper * dni(e.pred);   // сдвижка начала + сдвижка по этажам
        const ot = (e.tip === 'ОН' || e.tip === 'ОО' ? pg.ef : pg.es) + sdv;   // от окончания (ОН, ОО) или начала (НН, НО) предшественника
        const nach = e.tip === 'ОО' || e.tip === 'НО' ? ot - d : ot;          // ограничение на окончание → на начало
        if (nach > luchshee) { luchshee = nach; ved = e.pred; }
      });
      const es = Math.max(0, luchshee);
      const r: Rasch = { es, ef: es + d, ved, lf: 0, zapas: 0, krit: false };
      g.set(nm, r);
      return r;
    };
    poImeni.forEach((_, nm) => { sch(nm); });
    const konecCikla = Math.max(0, ...[...g.values()].map(x => x.ef));
    // позднее окончание = раннее из поздних сроков последователей; нет последователей — конец цикла
    const pozd = new Map<string, number>();
    const pv = new Set<string>();
    const lf = (nm: string): number => {
      const est = pozd.get(nm);
      if (est != null) return est;
      if (pv.has(nm)) return konecCikla;
      pv.add(nm);
      const dp = dni(nm);
      const sl = tverd.filter(e => e.pred === nm && g.has(e.work)).map(e => {
        const sdv = e.zapas + e.oper * dp;
        const lfS = lf(e.work), lsS = lfS - dni(e.work);
        // позже этого предшественник не может закончить (ОН, ОО) или начать (НН, НО)
        const gran = (e.tip === 'ОО' || e.tip === 'НО' ? lfS : lsS) - sdv;
        return e.tip === 'ОН' || e.tip === 'ОО' ? gran : gran + dp;
      });
      const x = sl.length ? Math.min(...sl) : konecCikla;
      pozd.set(nm, x);
      return x;
    };
    g.forEach((x, nm) => { x.lf = lf(nm); x.zapas = Math.max(0, x.lf - x.ef); x.krit = x.zapas === 0 && dni(nm) > 0; });
    return { g, kritDlit: konecCikla, konecDn: Math.max(10, konecCikla) };
  }, [R, poImeni, svyazi]);

  // ширина Ганта — остаток экрана справа от столбцов таблицы; длинный цикл листается вбок
  const [shir, setShir] = useState({ wrap: 1400, levo: 1000 });
  const [vys, setVys] = useState(600);
  useLayoutEffect(() => {
    const el = ekran.current;
    if (!el) return;
    const f = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      setVys(Math.max(360, Math.floor(window.innerHeight - top - 14)));
    };
    f();
    window.addEventListener('resize', f);
    // панель фильтров над экраном меняет высоту после загрузки — окно подстраивается
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(f) : null;
    if (ro && el.parentElement) [...el.parentElement.children].forEach(x => { if (x !== el) ro.observe(x); });
    return () => { window.removeEventListener('resize', f); ro?.disconnect(); };
  }, []);
  const mesto = Math.max(340, shir.wrap - shir.levo - 4);
  const PX = Math.max(4, Math.min(16, (mesto - 60) / rasch.konecDn));
  const gShag = [1, 2, 5, 10, 20, 30, 50, 100].find(k => k * PX >= 36) || 100;
  const x = (d: number) => OTS + d * PX;
  const gw = Math.max(mesto, x(rasch.konecDn) + 50);

  // после каждой отрисовки: замерить место под Гант и провести стрелки связей между полосами
  useLayoutEffect(() => {
    const w = wrap.current, t = tw.current, s = svg.current;
    if (!w || !t || !s) return;
    const th = t.querySelector<HTMLElement>('th.rb-gh');
    const levo = th ? th.offsetLeft : 1000;
    if (Math.abs(levo - shir.levo) > 2 || Math.abs(w.clientWidth - shir.wrap) > 2) setShir({ wrap: w.clientWidth, levo });
    const k = t.getBoundingClientRect();
    const poz = new Map<string, { l: number; r: number; y: number }>();
    t.querySelectorAll<HTMLElement>('[data-gb]').forEach(el => {
      const r = el.getBoundingClientRect();
      poz.set(el.dataset.gb!, { l: r.left - k.left, r: r.right - k.left, y: r.top + r.height / 2 - k.top });
    });
    s.setAttribute('width', String(t.scrollWidth));
    s.setAttribute('height', String(t.scrollHeight));
    s.innerHTML = svyazi.map(e => {
      const pa = poz.get(e.pred), pb = poz.get(e.work);
      if (!pa || !pb) return '';
      const gb = rasch.g.get(e.work), ga = rasch.g.get(e.pred);
      const ved = gb?.ved === e.pred;
      const kr = ved && !!ga?.krit && !!gb?.krit;
      // от начала (НН, НО) или окончания (ОН, ОО) предшественника — к началу (ОН, НН) или окончанию (ОО, НО) последователя
      const fx = e.tip === 'НН' || e.tip === 'НО' ? pa.l : pa.r;
      const kNach = e.tip !== 'ОО' && e.tip !== 'НО';
      const d = !kNach
        ? `M${fx},${pa.y} H${Math.max(fx, pb.r) + 8} V${pb.y} H${pb.r + 1}`
        : pb.l - fx >= 12
          ? `M${fx},${pa.y} H${pb.l - 8} V${pb.y} H${pb.l - 1}`
          : `M${fx},${pa.y} H${fx + (fx === pa.l ? -6 : 6)} V${pb.y - 9} H${pb.l - 7} V${pb.y} H${pb.l - 1}`;
      return `<path class="rb-gl${ved ? ' ved' : ''}${kr ? ' krit' : ''}${e.soft ? ' soft' : ''}" d="${d}" marker-end="url(#${kr ? 'rbArK' : 'rbAr'})"></path>`;
    }).join('')
      + '<defs><marker id="rbAr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" class="rb-ar"/></marker>'
      + '<marker id="rbArK" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" class="rb-ar krit"/></marker></defs>';
  });

  // ---- правки структуры: всё через хук (одна правка — один пакет на сервер) ----
  /** один уровень (владелец 2026-10-16, «убираем дерево»): раздел, из которого ушли все работы, убирается —
      иначе по правилам данных строка без детей стала бы работой */
  const bezPustyh = (byli: IerStroka[], n: IerStroka[]) => {
    const razd = new Set(byli.filter((_, i) => estDeti(byli, i)).map(r => r.klyuch));
    const ubrat = n.filter((r, i) => razd.has(r.klyuch) && !estDeti(n, i));
    if (!ubrat.length) return n;
    const kl = new Set(ubrat.map(r => r.klyuch));
    window.setTimeout(() => toast('Этап ' + ubrat.map(r => '«' + r.name + '»').join(', ') + ' опустел и убран'), 0);
    return n.filter(r => !kl.has(r.klyuch));
  };
  const pravka = (f: (rs: IerStroka[]) => IerStroka[], srazu = false) =>
    ie.pravka(cur => { const b = nastoyashchie(cur); return norm(bezPustyh(b, f(b))); }, srazu);

  function nachatPravku(k: string) {
    if (!can) return;
    setPravim(k);
    setDnAkt(null);
  }
  function zakonchitImya(k: string, value: string) {
    const otm = otmenaImeni.current;
    otmenaImeni.current = false;
    setPravim(cur => (cur === k ? null : cur));
    if (otm) return;
    const i = idxK(k);
    if (i < 0) return;
    const r = R[i];
    const v = value.trim();
    if (v === r.name.trim()) return;
    if (!v) { toast('Название не может быть пустым'); return; }
    if (!deti[i] && R.some((x, j) => j !== i && !deti[j] && (x.name.trim() === v || x.imyaSohr === v))) { toast('Работа «' + v + '» уже есть'); return; }
    pravka(rs => rs.map(x => (x.klyuch === k ? { ...x, name: v } : x)));
    toast((deti[i] ? 'Этап переименован' : 'Работа переименована') + ': «' + v + '»');
  }

  /** Вправо (Tab): работы верхнего уровня — в раздел строки выше. Один уровень: раздел внутрь раздела
      не кладётся, работа разделом не становится (владелец 2026-10-16) */
  function vpravo(keys = vybor) {
    if (!can) return;
    const kl = new Set(keys.filter(k => { const i = idxK(k); return i >= 0 && !deti[i] && R[i].glubina === 0; }));
    if (!kl.size) { toast(keys.some(k => deti[idxK(k)]) ? 'Этап внутрь этапа не кладётся — этапы только верхнего уровня' : 'Работа уже в этапе'); return; }
    const pervaya = R.findIndex(r => kl.has(r.klyuch));
    if (pervaya <= 0 || (R[pervaya - 1].glubina === 0 && !deti[pervaya - 1])) { toast('Над строкой нет этапа — создайте его кнопкой «＋ Этап»'); return; }
    let razd = pervaya - 1;
    while (razd > 0 && R[razd].glubina > 0) razd--;
    pravka(rs => {
      // выделенные подряд под разделом получают уровень 1; разрыв (обычная работа между ними) — останавливает
      const n = rs.slice();
      let i = n.findIndex(r => kl.has(r.klyuch));
      while (i >= 0 && i < n.length && (kl.has(n[i].klyuch) || n[i].glubina > 0)) {
        if (kl.has(n[i].klyuch)) n[i] = { ...n[i], glubina: 1 };
        i++;
      }
      return n;
    });
    if (ie.svernuto.has(R[razd].klyuch)) ie.svernut(R[razd].klyuch);
    toast('В этапе «' + R[razd].name + '»');
  }
  /** Влево (Shift+Tab): работы из раздела — на верхний уровень, сразу после раздела */
  function vlevo(keys = vybor) {
    if (!can) return;
    const kl = new Set(keys.filter(k => { const i = idxK(k); return i >= 0 && !deti[i] && R[i].glubina > 0; }));
    if (!kl.size) { toast('Выносить некуда: строка уже на верхнем уровне'); return; }
    pravka(rs => {
      const out: IerStroka[] = [];
      let vyn: IerStroka[] = [];
      rs.forEach(r => {
        if (r.glubina === 0) { out.push(...vyn); vyn = []; out.push(r); }
        else if (kl.has(r.klyuch)) vyn.push({ ...r, glubina: 0 });
        else out.push(r);
      });
      return [...out, ...vyn];
    });
  }
  /** выше / ниже — только среди строк своего раздела (иначе строка молча сменила бы раздел) */
  function mozhnoVverh(rs: IerStroka[], keys: string[]) {
    const bl = bloki(rs, keys);
    if (!bl.length || bl[0][0] === 0) return -1;
    const a = bl[0][0];
    let pp = a - 1;
    while (pp > 0 && rs[pp].glubina > rs[a].glubina) pp--;
    return rs[pp].glubina === rs[a].glubina ? pp : -1;
  }
  function mozhnoVniz(rs: IerStroka[], keys: string[]) {
    const bl = bloki(rs, keys);
    if (!bl.length) return false;
    const a = bl[0][0], z = bl[bl.length - 1][1];
    return z < rs.length && rs[z].glubina === rs[a].glubina;
  }
  function peremestit(vverh: boolean) {
    if (!can) return;
    const keys = vybor;
    pravka(rs => {
      const bl = bloki(rs, keys);
      if (!bl.length) return rs;
      const a = bl[0][0], z = bl[bl.length - 1][1];
      const kusok = rs.slice(a, z);
      if (vverh) {
        const pp = mozhnoVverh(rs, keys);
        if (pp < 0) return rs;
        return [...rs.slice(0, pp), ...kusok, ...rs.slice(pp, a), ...rs.slice(z)];
      }
      if (!mozhnoVniz(rs, keys)) return rs;
      const nz = konecPoddereva(rs, z);
      return [...rs.slice(0, a), ...rs.slice(z, nz), ...kusok, ...rs.slice(nz)];
    }, true);
  }
  /** ＋ Раздел: новый раздел верхнего уровня, выделенные работы — в него. Стоит на месте первой выделенной
      работы, а если она в разделе — сразу после того раздела (разделы только верхнего уровня, 2026-10-16) */
  function razdel() {
    if (!can) return;
    const rab = vybor.filter(k => { const i = idxK(k); return i >= 0 && !deti[i]; });
    if (!rab.length) { toast('Выделите работы, которые войдут в новый этап'); return; }
    let name = 'Новый этап', n = 2;
    while (R.some(r => r.name.trim() === name)) name = 'Новый этап ' + (n++);
    const nov = novayaStroka(name, 0);
    const kl = new Set(rab);
    pravka(rs => {
      const a = rs.findIndex(r => kl.has(r.klyuch));
      if (a < 0) return rs;
      let pos = a;
      if (rs[a].glubina > 0) { let p = a; while (p > 0 && rs[p].glubina > 0) p--; pos = konecPoddereva(rs, p); }
      const yakorK = pos < rs.length ? rs[pos].klyuch : null;
      const vybr = rs.filter(r => kl.has(r.klyuch)).map(r => ({ ...r, glubina: 1 }));
      const ost = rs.filter(r => !kl.has(r.klyuch));
      // якорь сам мог быть выделен — тогда ставим на место первой невыделенной строки после него
      let k = yakorK && !kl.has(yakorK) ? ost.findIndex(r => r.klyuch === yakorK) : -1;
      if (k < 0) { k = ost.length; for (let j = pos; j < rs.length; j++) if (!kl.has(rs[j].klyuch)) { k = ost.findIndex(r => r.klyuch === rs[j].klyuch); break; } }
      return [...ost.slice(0, k), nov, ...vybr, ...ost.slice(k)];
    }, true);
    setVybor([nov.klyuch]);
    yakor.current = nov.klyuch;
    setPravim(nov.klyuch);
    toast('Этап добавлен — введите название, Enter');
  }
  // ---- те же кнопки в группировке по признаку (2026-10-16): группа = значение признака, один уровень ----
  const grupMozhno = !svoya && grup !== 'net' && 'patch' in gDef;
  const vybVPoryadke = () => pokazIdx.filter(i => vybor.includes(R[i].klyuch) && !deti[i]);
  const gruppaStroki = (i: number) => gruppy.find(g => g.idx.includes(i)) || null;
  /** Вправо: работа без значения — в группу выше («— не задано —» стоит последней) */
  function vpravoGr() {
    const vs = vybVPoryadke();
    if (!vs.length || !grupMozhno) return;
    const g0 = gruppaStroki(vs[0]);
    if (g0 && g0.val != null) { toast('Работа уже в группе «' + g0.name + '» — «Влево» уберёт её из группы'); return; }
    const vyshe = g0 ? gruppy[gruppy.indexOf(g0) - 1] : null;
    if (!vyshe || vyshe.val == null) { toast('Над строкой нет группы — создайте её кнопкой «＋ Группа»'); return; }
    void smenitPriznak(R[vs[0]].klyuch, vyshe.val);
  }
  /** Влево: убрать значение признака — работа уходит в «— не задано —» */
  function vlevoGr() {
    const vs = vybVPoryadke();
    if (!vs.length || !grupMozhno) return;
    void smenitPriznak(R[vs[0]].klyuch, null);
  }
  /** Выше / ниже в группе: порядок общий с основной разбивкой, поэтому меняемся местами только с соседом
      из того же раздела «Этапов» — иначе работа молча сменила бы этап */
  const sosedGr = (vverh: boolean): number => {
    const vs = vybVPoryadke();
    if (vs.length !== 1) return -1;
    const i = vs[0];
    const spisok = grup === 'net' ? pokazIdx : (gruppaStroki(i)?.idx || []);
    const nb = spisok[spisok.indexOf(i) + (vverh ? -1 : 1)];
    if (nb == null || R[nb].glubina !== R[i].glubina) return -1;
    return roditel(R, nb) === roditel(R, i) ? nb : -1;
  };
  function peremestitGr(vverh: boolean) {
    if (!can) return;
    const vs = vybVPoryadke();
    if (vs.length !== 1) { toast('Выше / ниже в группировке — по одной работе'); return; }
    const nb = sosedGr(vverh);
    if (nb < 0) { toast('Соседняя работа в другом этапе «' + osnName + '» — порядок можно менять только в пределах этапа'); return; }
    const k = R[vs[0]].klyuch, kn = R[nb].klyuch;
    pravka(rs => {
      const ost = rs.filter(r => r.klyuch !== k);
      const ja = ost.findIndex(r => r.klyuch === kn);
      const sam = rs.find(r => r.klyuch === k);
      if (ja < 0 || !sam) return rs;
      const kuda = vverh ? ja : ja + 1;
      return [...ost.slice(0, kuda), sam, ...ost.slice(kuda)];
    }, true);
  }
  /** ＋ Группа: новое значение признака в «Списки», выделенные работы — в него */
  // «＋ Группа» — строкой в таблице над выделенными работами, как «＋ Этап» (владелец 2026-10-17:
  // «почему открывается отдельное окно, а не просто добавляется строчка»); null — строки нет
  const [novGr, setNovGr] = useState<string | null>(null);
  function nachatGruppu() {
    if (!can || !grupMozhno || !('kod' in gDef)) return;
    if (!vybVPoryadke().length) { toast('Выделите работы, которые войдут в новую группу'); return; }
    setNovGr('');
  }
  async function novGruppa(imya: string) {
    setNovGr(null);
    if (!can || !grupMozhno || !('kod' in gDef)) return;
    const vs = vybVPoryadke();
    if (!vs.length) { toast('Выделите работы, которые войдут в новую группу'); return; }
    const zn = imya.trim();
    if (!zn) return;
    const est = spisokZnach.find(z => z.toLowerCase() === zn.toLowerCase());
    if (!est && gDef.k !== 'stroka') {   // строку ГПР сервер заведёт сам при переносе работы
      const res = await call('post', p('/api/projects/{pid}/spiski', { pid: ctx.pid }), { kod: gDef.kod, znachenie: zn });
      if (!res.ok && res.error.code !== 'dup') { ctx.onError?.(res.error.message); return; }
    }
    await smenitPriznak(R[vs[0]].klyuch, est || zn);
    await zagruzitRaboty();
  }
  /** Разбить (владелец 2026-10-26, TZ_STROKI_GPR.md): работа становится строкой ГПР со своим названием,
      рядом с ней заводятся подработы — новые работы справочника с её признаками (раскатка, корпуса, порядок
      этажей, дни…), у каждой свои задачи, факт в шахматке и объём; все — в той же строке ГПР */
  // строка ввода подработ (владелец 2026-10-26: «вместо окна — работа станет жирной и сдвинется влево, снизу новая
  // строка с курсором»): stroka — куда встают, obrazec — работа, чьи признаки получают подработы
  const podrPosle = useRef(0);                              // за какой работой встаёт следующая подработа в «Этапах»
  const podrOchered = useRef<Promise<void>>(Promise.resolve());   // быстрый набор — подработы заводятся по очереди
  async function razbit() {
    if (!can) return;
    const vs = vybVPoryadke();
    const r = vs.length === 1 ? R[vs[0]] : null;
    const w = r?.work_id ? wk.get(r.work_id) : undefined;
    if (!r || !w) { toast('Выделите одну работу, которую нужно разбить на подработы'); return; }
    const stroka = w.stroka_gpr || w.name;
    if (!w.stroka_gpr) {
      setSvBusy(b => b + 1);
      try {
        const res = await call('patch', p('/api/works/{wid}', { wid: w.id }), { stroka_gpr: stroka });
        if (!res.ok) { toast(res.error.message); ctx.onError?.(res.error.message); return; }
        await zagruzitRaboty();
      } finally { setSvBusy(b => b - 1); }
    }
    setSvGr(m => { const n = new Set(m); n.delete('grp:' + stroka); return n; });
    setVybor([r.klyuch]); yakor.current = r.klyuch;
    // новые — за последней работой строки в порядке «Этапов»
    const ids = R.filter(x => x.work_id && wk.get(x.work_id)?.stroka_gpr === stroka).map(x => x.work_id!);
    podrPosle.current = ids.length ? ids[ids.length - 1] : w.id;
    setPodr({ stroka, obrazec: w.id });
  }
  /** Enter в строке ввода: подработа — новая работа с признаками образца, сразу в ту же строку ГПР */
  function dobavitPodrabotu(imya: string) {
    const nm = imya.trim();
    const cel = podr;
    const w = cel ? wk.get(cel.obrazec) : undefined;
    if (!nm || !cel || !w) return;
    podrOchered.current = podrOchered.current.then(async () => {
      setSvBusy(b => b + 1);
      try {
        const res = await call<{ id: number }>('post', p('/api/projects/{pid}/works', { pid: ctx.pid }), {
          name: nm, posle_work_id: podrPosle.current, mesto: w.mesto, logika_montazha: w.logika, dlitelnost: w.dlit,
          priznak_tk: w.tk, resurs_imya: w.resurs, resurs_kolvo: w.chel, trudoemkost: w.trud, uroven2: w.sistema,
          kontur: w.kontur, ed_izm: w.ed_izm, korpusa: w.korpusa, raskatka: w.raskatka, kalendar: w.kalendar });
        if (!res.ok) { toast('«' + nm + '»: ' + res.error.message); ctx.onError?.(res.error.message); return; }
        podrPosle.current = res.data.id;
        podrZhdut.current.set(res.data.id, cel.stroka);
        const r2 = await call('patch', p('/api/works/{wid}', { wid: res.data.id }), { stroka_gpr: cel.stroka });
        if (!r2.ok) { toast('«' + nm + '»: ' + r2.error.message); ctx.onError?.(r2.error.message); }
      } finally {
        setSvBusy(b => b - 1);
        ctx.onChanged?.();   // перечитать структуру и работы: подработа появится над строкой ввода
      }
    });
  }
  /** после какой строки экрана стоит строка ввода: последняя видимая работа строки podr.stroka (на любом виде
      «Работ» — «Разбить» есть везде, владелец 2026-10-26), в «Строке в ГПР» — и заголовок её группы */
  const podrMesto = useMemo(() => {
    if (!podr) return -2;
    let last = -1;
    pokaz.forEach((st, j) => {
      if (st.gr) { if (grup === 'stroka' && st.gr.val === podr.stroka) last = j; return; }
      const wid = R[st.i]?.work_id;
      if (!wid) return;
      if (wid === podr.obrazec || wk.get(wid)?.stroka_gpr === podr.stroka || podrZhdut.current.get(wid) === podr.stroka) last = j;
    });
    return last;
  }, [podr, pokaz, grup, R, wk]);
  /** отступ строки ввода — как у разбиваемой работы, на уровень глубже */
  const podrOtstup = useMemo(() => {
    if (!podr || grup === 'stroka') return 20;
    const r = R.find(x => x.work_id === podr.obrazec);
    return ((r ? G(r) : 0) + 1) * 20;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podr, grup, R]);
  // одинаковые кнопки: в разбивке — разделы, в группировке — значение признака
  const vlevoV = () => (svoya ? vlevo() : vlevoGr());
  const vpravoV = () => (svoya ? vpravo() : vpravoGr());

  async function udalit(sprosit = false) {
    if (!can) return;
    const bl = bloki(R, vybor);
    if (!bl.length) return;
    const ud = bl.flatMap(([a, z]) => R.slice(a, z));
    const zhalko = ud.filter(r => R.indexOf(r) >= 0 && !deti[R.indexOf(r)] && r.work_id && !r.netronuta);
    if (zhalko.length) {
      const ok = await confirmBox('Удалить строк: ' + ud.length + '? Из графика уйдут работы ' + zhalko.slice(0, 5).map(r => '«' + r.name + '»').join(', ')
        + (zhalko.length > 5 ? ' и ещё ' + (zhalko.length - 5) : '') + ' — их задачи (' + zhalko.reduce((s, r) => s + r.zadach, 0)
        + '), факт, ручные даты и связи. Отменить будет нельзя.');
      if (!ok) return;
    } else if (sprosit) {
      const ok = await confirmBox(ud.length === 1 ? 'Удалить «' + ud[0].name + '»' + (deti[R.indexOf(ud[0])] ? '' : ' вместе с её задачами') + '?'
        : 'Удалить строк: ' + ud.length + ' (' + ud.slice(0, 5).map(r => '«' + r.name + '»').join(', ') + (ud.length > 5 ? ' и ещё ' + (ud.length - 5) : '') + ')?');
      if (!ok) return;
    }
    const kl = new Set(ud.map(r => r.klyuch));
    pravka(rs => rs.filter(x => !kl.has(x.klyuch)), true);
    setVybor([]);
    yakor.current = null;
    toast('Удалено строк: ' + ud.length);
  }
  function svernutVse(da: boolean) {
    if (!svoya) { setSvGr(da ? new Set(gruppy.map(g => g.k)) : new Set()); return; }
    R.forEach((r, i) => { if (deti[i] && ie.svernuto.has(r.klyuch) !== da) ie.svernut(r.klyuch); });
  }

  // ---- связи: создать, удалить, связать цепочкой, отменить ----
  async function sozdatSv(pary: { pred: string; work: string }[]): Promise<number[]> {
    if (!pary.length) return [];
    let t = tid;
    setSvBusy(b => b + 1);
    // сразу в таблице, сервер — фоном; при ошибке список перечитывается
    setSv(cur => [...cur, ...pary.map((x, k) => ({ id: -1 - k, work: x.work, pred: x.pred, tip: 'ОН', zapas: 0, oper: 0, soft: false }))]);
    const ids: number[] = [];
    try {
      if (!t) {
        const c = await call<{ id: number }>('post', '/api/link-templates', { name: 'Базовый', project: ctx.pid });
        if (!c.ok) { toast(c.error.message); ctx.onError?.(c.error.message); return []; }
        t = c.data.id;
        setTid(t);
      }
      for (const x of pary) {
        const r = await call<{ id: number }>('post', p('/api/link-templates/{tid}/rows', { tid: t }), { work: x.work, pred: x.pred });
        if (!r.ok) { toast(r.error.message); ctx.onError?.(r.error.message); break; }
        ids.push(r.data.id);
      }
    } finally {
      await zagruzitSv();
      setSvBusy(b => b - 1);
    }
    return ids;
  }
  async function udalitSv(ids: number[]) {
    const nastoyashchieIds = ids.filter(id => id > 0);
    if (!nastoyashchieIds.length) return;
    setSvBusy(b => b + 1);
    setSv(cur => cur.filter(e => !nastoyashchieIds.includes(e.id)));
    try {
      for (const id of nastoyashchieIds) {
        const r = await call('delete', p('/api/template-rows/{rid}', { rid: id }));
        if (!r.ok) { toast(r.error.message); ctx.onError?.(r.error.message); break; }
      }
    } finally {
      await zagruzitSv();
      setSvBusy(b => b - 1);
    }
  }
  async function dobavitSv(pred: string, work: string) {
    if (pred === work || sv.some(e => e.pred === pred && e.work === work)) return;
    if (krugIz(work, pred)) { toast('Так получится круг: эта работа уже идёт после той'); return; }
    toast('«' + work + '» идёт после «' + pred + '»');
    await sozdatSv([{ pred, work }]);
  }
  // ---- окно настройки связи ----
  const [okno, setOkno] = useState<{ id: number; at: { left: number; top: number; bottom: number }; n: number; posle?: boolean } | null>(null);
  const [svSbros, setSvSbros] = useState(0);
  const svOchered = useRef<Promise<void>>(Promise.resolve());   // правки связи — по очереди, ответы не обгоняют друг друга
  const oknoE = okno ? sv.find(x => x.id === okno.id) || null : null;
  useEffect(() => { if (okno && !oknoE) setOkno(null); }, [okno, oknoE]);
  useEffect(() => {
    if (!okno) return;
    // щелчок мимо окна закрывает его (по тегу — откроется окно этого тега)
    const f = (ev: MouseEvent) => {
      const t = ev.target as HTMLElement | null;
      if (t?.closest?.('.rb-svpop, .rb-teg, .dlg')) return;
      setOkno(null);
    };
    document.addEventListener('mousedown', f, true);
    return () => document.removeEventListener('mousedown', f, true);
  }, [okno]);
  /** правка поля связи: сразу на экране (тег, Гант), затем PATCH; отказ — вернуть прежнее */
  function pravSv(id: number, pole: SvPole, v: string | number | boolean) {
    const e = sv.find(x => x.id === id);
    if (!e || id < 0 || !can) return;
    const bylo = e[pole];
    setSv(cur => cur.map(x => (x.id === id ? { ...x, [pole]: v } : x)));
    const telo = pole === 'oper' ? { operezhenie_etazhej: v } : { [pole]: v };
    svOchered.current = svOchered.current.then(async () => {
      setSvBusy(b => b + 1);
      const r = await call('patch', p('/api/template-rows/{rid}', { rid: id }), telo);
      setSvBusy(b => b - 1);
      if (!r.ok) {
        toast(r.error.message);
        ctx.onError?.(r.error.message);
        setSv(cur => cur.map(x => (x.id === id ? { ...x, [pole]: bylo } : x)));
        setSvSbros(n => n + 1);
      }
    });
  }
  async function snyatSv(e: Svyaz) {
    toast('«' + e.work + '» больше не ждёт «' + e.pred + '»');
    await udalitSv([e.id]);
  }
  async function svyazat() {
    if (!can) return;
    const v = vybRaboty;
    if (v.length < 2) return;
    const novye: { pred: string; work: string }[] = [];
    let uzhe = 0, krug = 0;
    for (let j = 1; j < v.length; j++) {
      const a = imyaSv(v[j - 1]), b = imyaSv(v[j]);
      if (sv.some(e => e.pred === a && e.work === b) || novye.some(e => e.pred === a && e.work === b)) { uzhe++; continue; }
      if (krugIz(b, a, [...sv, ...novye])) { krug++; continue; }
      novye.push({ pred: a, work: b });
    }
    const bylVybor = v.map(r => r.klyuch);
    setVybor([]);
    yakor.current = null;
    toast((novye.length ? 'Ctrl+Z — отменить · ' : '') + 'Связано: ' + novye.length + (uzhe ? ' · уже было: ' + uzhe : '')
      + (krug ? ' · пропущено (дали бы круг): ' + krug : '') + ' — «' + v.map(r => r.name).join(' → ') + '»');
    otmena.current = null;
    const ids = await sozdatSv(novye);
    otmena.current = ids.length ? { ids, vybor: bylVybor } : null;
  }
  async function otmenitSvyazat() {
    const o = otmena.current;
    if (!o) return;
    otmena.current = null;
    setVybor(o.vybor);
    toast('Связывание отменено: убрано связей ' + o.ids.length + ', выделение вернул');
    await udalitSv(o.ids);
  }
  async function razorvat() {
    if (!can) return;
    const imena = new Set(vybRaboty.map(imyaSv));
    const ud = svyazi.filter(e => imena.has(e.pred) && imena.has(e.work));
    toast('Убрано связей: ' + ud.length);
    await udalitSv(ud.map(e => e.id));
  }

  // ---- «Дней» как ячейка Excel ----
  const dnRyady = () => pokazIdx.filter(i => !deti[i]).map(i => R[i]);
  function dnVybrat(k: string) {
    setDnAkt(k);
    if (!(vybor.length > 1 && vybor.includes(k))) { setVybor([k]); yakor.current = k; }   // выделенную группу не сбрасываем — для правки всем
  }
  function dnNachat(k: string, start: string) {
    if (!can) return;
    const r = R[idxK(k)];
    if (!r || !r.work_id) { toast('Работа ещё сохраняется — дни можно будет задать через секунду'); return; }
    dnZakryto.current = false;
    setDnAkt(k);
    setDnPravka({ k, start });
  }
  async function dnSohr(value: string, vniz: boolean) {
    const pr = dnPravka;
    if (!pr || dnZakryto.current) return;
    dnZakryto.current = true;
    setDnPravka(null);
    const i = idxK(pr.k);
    if (i < 0) return;
    const r = R[i];
    const t = value.trim().replace(',', '.');
    if (t && (isNaN(+t) || +t < 0)) { toast('Длительность — число дней'); return; }
    const komu = vybor.includes(r.klyuch) && vybor.length > 1 ? vybRaboty : [r];
    if (vniz) {
      const vs = dnRyady();
      const k = vs.findIndex(x => x.klyuch === r.klyuch);
      if (k >= 0 && k + 1 < vs.length) {
        setDnAkt(vs[k + 1].klyuch);
        if (komu.length < 2) { setVybor([vs[k + 1].klyuch]); yakor.current = vs[k + 1].klyuch; }
      }
    }
    if (komu.length > 1) toast('Применено к ' + komu.length + ' работам: ' + (t === '' ? 'пусто' : t + ' дн'));
    for (const w of komu) await ie.smenitDlit(w, t);
  }
  async function dnOchistit() {
    const i = dnAkt ? idxK(dnAkt) : -1;
    if (i < 0 || !can) return;
    const komu = vybor.includes(R[i].klyuch) && vybor.length > 1 ? vybRaboty : [R[i]];
    if (komu.length > 1) toast('Дни очищены у ' + komu.length + ' работ');
    for (const w of komu) await ie.smenitDlit(w, '');
  }

  // ---- Enter на работе: новая строка под ней ----
  function vstNachat(k: string) {
    const i = idxK(k);
    if (i < 0 || deti[i]) return;
    setVst({ posle: k, val: !svoya && grup !== 'net' ? znachRaboty(R[i]) : null });
    setDnAkt(null);
  }
  function vstEnter(nmRaw: string) {
    const v = vst;
    if (!v) return;
    const nm = nmRaw.trim();
    if (!nm) { setVst(null); return; }
    if (R.some((x, j) => !deti[j] && (x.name.trim() === nm || x.imyaSohr === nm))) { toast('Работа «' + nm + '» уже есть'); return; }
    const a = idxK(v.posle);
    if (a < 0) { setVst(null); return; }
    const nov = novayaStroka(nm, R[a].glubina);
    if (!svoya && grup !== 'net' && 'patch' in gDef && v.val != null) zhdyotPriznak.current.set(nov.klyuch, { patch: gDef.patch, val: v.val });
    pravka(rs => {
      const i = rs.findIndex(x => x.klyuch === v.posle);
      return i < 0 ? [...rs, nov] : [...rs.slice(0, i + 1), nov, ...rs.slice(i + 1)];
    }, true);
    setVybor([nov.klyuch]);
    yakor.current = nov.klyuch;
    setVst({ posle: nov.klyuch, val: v.val });   // следующая строка — под только что добавленной
    toast('«' + nm + '» добавлена — можно сразу вписать следующую, Esc — закончить');
  }
  // признак группы — новой работе, как только сервер выдал ей номер
  useEffect(() => {
    const m = zhdyotPriznak.current;
    if (!m.size) return;
    R.forEach(r => {
      const zh = m.get(r.klyuch);
      if (!zh || zh.ushlo || !r.work_id) return;
      zh.ushlo = true;   // пока признак не вернулся с сервером, работа остаётся в своей группе
      void call('patch', p('/api/works/{wid}', { wid: r.work_id }), { [zh.patch]: zh.val })
        .then(async res => { if (!res.ok) ctx.onError?.(res.error.message); await zagruzitRaboty(); m.delete(r.klyuch); });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [R]);

  // ---- «＋ Работа»: черновик строки внизу ----
  const poslRabota = (() => { for (let i = R.length - 1; i >= 0; i--) if (!deti[i]) return R[i]; return null; })();
  function chernEnter() {
    const el = chernInp.current;
    if (!el) return;
    const nm = el.value.trim();
    if (!nm) return;
    if (R.some((x, j) => !deti[j] && (x.name.trim() === nm || x.imyaSohr === nm))) { toast('Работа «' + nm + '» уже есть'); return; }
    const g = R.length ? R[R.length - 1].glubina : 0;
    pravka(rs => [...rs, novayaStroka(nm, g)]);
    el.value = '';
    toast('«' + nm + '» добавлена' + (poslRabota ? ', дни, раскатка и порядок — как у «' + poslRabota.name + '»' : '') + '; задачи по этажам создаются на сервере');
  }
  useEffect(() => { if (chern) chernInp.current?.focus(); }, [chern]);

  // ---- перетаскивание строки за ручку ⠿: выше, ниже или внутрь раздела ----
  const [tyanem, setTyanem] = useState<string | null>(null);
  const [cel, setCel] = useState<{ k: string; kuda: Kuda } | null>(null);
  const celRef = useRef<{ k: string; kuda: Kuda } | null>(null);
  /** что тянем: строку, а если она среди выделенных — всё выделенное (владелец 2026-10-16) */
  const tyanemKl = useMemo(() => (!tyanem ? [] : vybor.includes(tyanem) && vybor.length > 1 ? vybor : [tyanem]), [tyanem, vybor]);
  const tyanemPod = useMemo(() => {
    if (!tyanem) return new Set<string>();
    return new Set(bloki(R, tyanemKl).flatMap(([a, z]) => R.slice(a, z).map(r => r.klyuch)));
  }, [tyanem, tyanemKl, R]);
  useEffect(() => {
    if (!tyanem) return;
    document.body.classList.add('rb-tyanem');
    const dvizh = (e: PointerEvent) => {
      const tr = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>('tr[data-k]');
      const k = tr?.dataset.k;
      if (!tr || !k || tyanemPod.has(k)) { celRef.current = null; setCel(null); return; }
      const i = R.findIndex(r => r.klyuch === k);
      const b = tr.getBoundingClientRect();
      const dol = (e.clientY - b.top) / b.height;
      // в группировке строка ложится в группу целиком — подсвечиваем цель «внутрь»
      let kuda: Kuda = !svoya || (i >= 0 && deti[i] && dol > 0.3 && dol < 0.7) ? 'vnutr' : dol < 0.5 ? 'verh' : 'niz';
      // раздел — только верхнего уровня (2026-10-16): не внутрь, не между работами раздела
      const ti = idxK(tyanem);
      if (svoya && ti >= 0 && deti[ti] && i >= 0) {
        if (R[i].glubina > 0) { celRef.current = null; setCel(null); return; }
        if (kuda === 'vnutr' || (kuda === 'niz' && deti[i] && !ie.svernuto.has(k))) kuda = 'verh';
      }
      if (celRef.current?.k !== k || celRef.current.kuda !== kuda) { celRef.current = { k, kuda }; setCel(celRef.current); }
    };
    const otpustil = () => {
      const c = celRef.current, id = tyanem;
      celRef.current = null;
      setCel(null);
      setTyanem(null);
      if (!c) return;
      if (!svoya) {
        // группировка по признаку: работа (и выделенные с ней) получает значение группы, куда её бросили;
        // структура (PUT /wbs) не трогается
        if (grup === 'net') { toast('Без группировки порядок не меняется — переставлять строки можно в «' + osnName + '»'); return; }
        const g = gruppy.find(x => x.k === c.k || x.idx.some(i => R[i].klyuch === c.k));
        if (!g) return;
        const svoyaGr = gruppy.find(x => x.idx.some(i => R[i].klyuch === id));
        if (svoyaGr === g && tyanemKl.length < 2) return;   // остальные выделенные могут быть в других группах
        void smenitPriznak(id, g.val);
        return;
      }
      const t = R[R.findIndex(r => r.klyuch === c.k)];
      const svern = ie.svernuto.has(c.k);
      if (c.kuda === 'vnutr' && svern) ie.svernut(c.k);
      const kls = tyanemKl.length ? tyanemKl : [id];
      // раздел среди перетаскиваемых — только на верхний уровень (один уровень, 2026-10-16)
      if (kls.length > 1 && kls.some(k => deti[idxK(k)]) && (c.kuda === 'vnutr' || (R[idxK(c.k)]?.glubina ?? 0) > 0)) {
        toast('Среди выделенных есть этап — этап внутрь этапа не кладётся'); return;
      }
      pravka(rs => {
        const bl = bloki(rs, kls);
        if (!bl.length) return rs;
        const vzyat = new Set(bl.flatMap(([a, z]) => rs.slice(a, z).map(x => x.klyuch)));
        // каждый блок сохраняет свой уровень относительно первой строки блока
        const kusok = bl.flatMap(([a, z]) => rs.slice(a, z).map(x => ({ ...x, glubina: x.glubina - rs[a].glubina })));
        const g0 = 0;
        const ost = rs.filter(x => !vzyat.has(x.klyuch));
        const ti = ost.findIndex(x => x.klyuch === c.k);
        if (ti < 0) return rs;
        const tt = ost[ti];
        let kuda: number, g: number;
        if (c.kuda === 'verh') { kuda = ti; g = tt.glubina; }
        else if (c.kuda === 'vnutr') { kuda = konecPoddereva(ost, ti); g = tt.glubina + 1; }
        else if (estDeti(ost, ti) && !svern) { kuda = ti + 1; g = tt.glubina + 1; }
        else { kuda = konecPoddereva(ost, ti); g = tt.glubina; }
        const nov = kusok.map(x => ({ ...x, glubina: x.glubina - g0 + g }));
        return [...ost.slice(0, kuda), ...nov, ...ost.slice(kuda)];
      }, true);
      if (kls.length > 1) { setVybor(kls); yakor.current = kls[0]; }
      else { setVybor([id]); yakor.current = id; }
      const imya = kls.length > 1 ? 'Выделенные (' + kls.length + ')' : '«' + (R[R.findIndex(r => r.klyuch === id)]?.name || '') + '»';
      toast(c.kuda === 'vnutr' && t ? imya + ' — в этапе «' + t.name + '»' : imya + ' — перенесено');
    };
    window.addEventListener('pointermove', dvizh);
    window.addEventListener('pointerup', otpustil);
    return () => { window.removeEventListener('pointermove', dvizh); window.removeEventListener('pointerup', otpustil); document.body.classList.remove('rb-tyanem'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyanem]);

  // ---- мышь по строке: выделение ----
  function naRyad(e: React.MouseEvent, k: string) {
    const t = e.target as HTMLElement;
    if (!t.closest('.rb-dt, input.rb-dn-i')) setDnAkt(null);   // щелчок в другом месте снимает выбор ячейки
    const s_ = e.ctrlKey || e.metaKey || e.shiftKey;
    // с Ctrl/Shift списки и поля в строке не открываются — щелчок только выделяет (владелец 2026-10-16)
    if (s_ && t.closest('select, input, .rb-teg')) { e.preventDefault(); (document.activeElement as HTMLElement | null)?.blur?.(); }
    else if (t.closest('input, select')) {
      // поле или список в строке вне выделения: строка становится выделенной, прежняя — сбрасывается
      // (владелец 2026-10-16); внутри выделения из нескольких — остаётся, правка уйдёт всем
      if (!(vybor.includes(k) && vybor.length > 1) && !(vybor.length === 1 && vybor[0] === k)) { setVybor([k]); yakor.current = k; }
      return;
    }
    else if (t.closest('.rb-kart-b, .rb-ruchka span, .rb-teg')) return;
    if (e.detail >= 2 && !s_) { e.preventDefault(); setVybor([k]); yakor.current = k; nachatPravku(k); return; }
    if (e.shiftKey && yakor.current != null) {
      e.preventDefault();
      const a = pokazIdx.indexOf(idxK(yakor.current)), b = pokazIdx.indexOf(idxK(k));
      if (a >= 0 && b >= 0) setVybor(pokazIdx.slice(Math.min(a, b), Math.max(a, b) + 1).map(j => R[j].klyuch));
      return;
    }
    if (e.ctrlKey || e.metaKey) { setVybor(v => (v.includes(k) ? v.filter(x => x !== k) : [...v, k])); yakor.current = k; return; }
    setVybor(v => (v.length === 1 && v[0] === k ? [] : [k]));
    yakor.current = k;
  }

  // ---- клавиатура вне полей ввода ----
  const klav = useRef<(e: KeyboardEvent) => void>();
  klav.current = (e: KeyboardEvent) => {
    if (document.querySelector('.dlg, .pw-pop')) return;   // открыто окно или карточка — клавиши им
    if (okno) { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOkno(null); } return; }
    const ae = document.activeElement as HTMLElement | null;
    if (ae?.closest?.('input, select, textarea')) return;   // у полей свои клавиши
    if (ae && ae !== document.body && !ekran.current?.contains(ae)) return;
    const k = e.key, ctrl = e.ctrlKey || e.metaKey;
    // выбранная ячейка «Дней»
    if (dnAkt && !dnPravka) {
      const r = R[idxK(dnAkt)];
      if (can && r && /^[0-9]$/.test(k) && !ctrl) { e.preventDefault(); dnNachat(dnAkt, k); return; }
      if (can && r && k === 'F2' && !ctrl) { e.preventDefault(); dnNachat(dnAkt, String(r.dlit ?? '')); return; }
      if (can && r && k === 'Enter' && !ctrl && !e.shiftKey && !(vybRaboty.length > 1)) { e.preventDefault(); dnNachat(dnAkt, String(r.dlit ?? '')); return; }
      if (can && (k === 'Delete' || k === 'Backspace')) { e.preventDefault(); void dnOchistit(); return; }
      if (k === 'ArrowDown' || k === 'ArrowUp') {
        e.preventDefault();
        const vs = dnRyady();
        const i = vs.findIndex(x => x.klyuch === dnAkt);
        const j = Math.max(0, Math.min(vs.length - 1, i + (k === 'ArrowDown' ? 1 : -1)));
        if (vs[j]) { setDnAkt(vs[j].klyuch); setVybor([vs[j].klyuch]); yakor.current = vs[j].klyuch; }
        return;
      }
      if (k === 'Escape') setDnAkt(null);   // дальше — и выделение снимется
    }
    // Delete — удалить выделенные строки, с вопросом (владелец 2026-10-16)
    if (can && k === 'Delete' && !ctrl && vybor.length && !dnAkt) { e.preventDefault(); void udalit(true); return; }
    // Enter на одной выбранной работе — новая работа под ней (владелец 2026-10-16)
    if (can && k === 'Enter' && !ctrl && !e.shiftKey && vybor.length === 1 && !dnAkt) {
      const i = idxK(vybor[0]);
      if (i >= 0 && !deti[i]) { e.preventDefault(); vstNachat(vybor[0]); return; }
    }
    // Enter (2+ выделенных работ) или Ctrl+F2, как в Project, — связать цепочкой; Ctrl+Z — отменить
    if (can && ((k === 'Enter' && !ctrl && !e.shiftKey && !param) || (k === 'F2' && ctrl)) && vybRaboty.length >= 2) { e.preventDefault(); void svyazat(); return; }
    if (can && ctrl && ['z', 'Z', 'я', 'Я'].includes(k) && otmena.current) { e.preventDefault(); void otmenitSvyazat(); return; }
    // Tab / Shift+Tab — вложить / поднять выделенные
    if (can && (svoya || grupMozhno) && k === 'Tab' && vybor.length) { e.preventDefault(); if (e.shiftKey) vlevoV(); else vpravoV(); return; }
    if (k === 'Escape' && vybor.length) { setVybor([]); yakor.current = null; return; }
    if (can && k === 'F2' && !ctrl && vybor.length === 1) { e.preventDefault(); nachatPravku(vybor[0]); }
  };
  useEffect(() => {
    const f = (e: KeyboardEvent) => klav.current?.(e);
    document.addEventListener('keydown', f, true);
    return () => document.removeEventListener('keydown', f, true);
  }, []);

  // ---- отрисовка ----
  const nomer = (nm: string) => (poImeni.get(nm) ?? -1) + 1;
  const raboty = [...poImeni.keys()];
  const sostTxt = svBusy > 0 || ie.sost === 'сохраняю' || ie.sost === 'правка' ? 'сохраняю…' : ie.sost === 'ошибка' ? 'не сохранилось' : ie.sost === 'сохранено' ? 'сохранено' : '';
  const est = vybor.length > 0;
  const vybIdx = vybor.map(idxK).filter(i => i >= 0).sort((a, b) => a - b);
  const razorvatMozhno = vybRaboty.length >= 2 && (() => { const im = new Set(vybRaboty.map(imyaSv)); return svyazi.some(e => im.has(e.pred) && im.has(e.work)); })();
  const vpravoMozhno = est && vybIdx.some(i => !deti[i] && R[i].glubina === 0) && vybIdx[0] > 0 && (deti[vybIdx[0] - 1] || R[vybIdx[0] - 1].glubina > 0);
  const shkala: JSX.Element[] = [];
  for (let d = 0; d <= rasch.konecDn + gShag; d += gShag) shkala.push(<span key={d} className="rb-gt" style={{ left: x(d) }}>{d}</span>);

  const tegi = (r: IerStroka, posle: boolean) => {
    const nm = imyaSv(r);
    const spisok = svyazi.filter(e => (posle ? e.pred === nm : e.work === nm));
    const uzhe = new Set(spisok.map(e => (posle ? e.work : e.pred)));
    // в списке — без самой работы, уже стоящих и тех, что дали бы круг
    // варианты списка строятся, когда мышь над «＋» (иначе сотни строк × сотни вариантов в разметке)
    const klS = r.klyuch + (posle ? ':po' : ':do');
    const mozhno = spisokDlya === klS ? raboty.filter(w => w !== nm && !uzhe.has(w) && !(posle ? krugIz(w, nm) : krugIz(nm, w))) : [];
    return (
      <td className="rb-pt" key={klS}><div className="rb-pt-in">
        {/* тег — как в MS Project: номер строки + тип (кроме ОН) + сдвижка; название — в подсказке
            (владелец 2026-10-16: «указывай только номер, тип связи, смещение — текст не влезает»).
            Больше 8 — «＋N ещё» */}
        {(raskryto.has(klS) ? spisok : spisok.slice(0, 8)).map(e => {
          const drug = posle ? e.work : e.pred;
          const pd = [e.tip !== 'ОН' ? e.tip : '', e.zapas ? (e.zapas > 0 ? '+' : '−') + Math.abs(e.zapas) + ' дн' : '', e.oper ? (e.oper > 0 ? '+' : '−') + Math.abs(e.oper) + ' эт.' : '', e.soft ? 'мягкая' : ''].filter(Boolean).join(' ');
          // коротко на теге: «7НН+2», «12ОО», «8 +1эт», «5~» (мягкая)
          const kor = (e.tip !== 'ОН' ? e.tip : '') + (e.zapas ? (e.zapas > 0 ? '+' : '−') + Math.abs(e.zapas) : '')
            + (e.oper ? ' ' + (e.oper > 0 ? '+' : '−') + Math.abs(e.oper) + 'эт' : '') + (e.soft ? '~' : '');
          return (
            <span key={e.id} className={'rb-teg' + (pd ? ' nastr' : '') + (okno?.id === e.id ? ' akt' : '')}
              title={'№' + nomer(drug) + ' ' + drug + (posle ? ' — идёт после этой' : '') + ' · ' + ({ ОН: 'окончание → начало', НН: 'начало → начало', ОО: 'окончание → окончание', НО: 'начало → окончание' } as Record<string, string>)[e.tip] + (pd ? ', ' + pd : '') + '. Щелчок — настроить связь'}
              onClick={ev => {
                if (ev.ctrlKey || ev.metaKey || ev.shiftKey) return;   // с Ctrl/Shift — только выделение строки
                const b = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                setOkno(o => (o?.id === e.id ? null : { id: e.id, at: { left: b.left, top: b.top, bottom: b.bottom }, n: (o?.n || 0) + 1, posle }));
              }}>
              <i>{nomer(drug)}</i>{kor && <em>{kor}</em>}
              {can && <b title="Убрать связь" onClick={ev => { ev.stopPropagation(); void snyatSv(e); }}>×</b>}
            </span>);
        })}
        {spisok.length > 8 && <span className="rb-teg-esche" onClick={() => setRaskryto(m => { const n = new Set(m); if (n.has(klS)) n.delete(klS); else n.add(klS); return n; })}
          title={raskryto.has(klS) ? 'Свернуть' : spisok.slice(8).map(e => '№' + nomer(posle ? e.work : e.pred) + ' ' + (posle ? e.work : e.pred)).join('\n') + '\nЩелчок — показать все'}>
          {raskryto.has(klS) ? 'свернуть' : '＋' + (spisok.length - 8) + ' ещё'}</span>}
        {/* «＋» — поле поиска работы по номеру или названию (владелец 2026-10-16: «должен быть
            выпадающий список, где можно искать нужную работу») */}
        {can && (spisokDlya === klS
          ? <PoiskRaboty varianty={mozhno.map(w => ({ w, n: nomer(w) }))}
              placeholder={posle ? 'последователь: № или название' : 'предшественник: № или название'}
              onVybor={w => { setSpisokDlya(null); void (posle ? dobavitSv(nm, w) : dobavitSv(w, nm)); }}
              onZakryt={() => setSpisokDlya(null)} />
          : <span className="rb-teg-add" title={posle ? 'Добавить работу, которая идёт после этой' : 'Добавить работу, после которой идёт эта'}
              onMouseDown={ev => { if (ev.ctrlKey || ev.metaKey || ev.shiftKey) return; ev.stopPropagation(); ev.preventDefault(); setSpisokDlya(klS); }}>＋</span>)}
      </div></td>);
  };

  // ---- связь мышью на Ганте (владелец 2026-10-16): тянуть от полосы к строке другой работы, как в Project ----
  const [tyanuSv, setTyanuSv] = useState<{ ot: string; x0: number; y0: number; x: number; y: number; cel: string | null } | null>(null);
  const celSv = (x: number, y: number, ot: string): string | null => {
    const tr = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('tr[data-k]');
    const i = tr ? idxK(tr.dataset.k!) : -1;
    if (i < 0 || deti[i]) return null;
    const nm = imyaSv(R[i]);
    return nm === ot ? null : nm;
  };
  function svNachat(ev: React.PointerEvent, ot: string) {
    if (!can || ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey) return;
    ev.preventDefault();
    const x0 = ev.clientX, y0 = ev.clientY;
    let tek = { ot, x0, y0, x: x0, y: y0, cel: null as string | null };
    setTyanuSv(tek);
    document.body.classList.add('rb-svyazyvaem');
    const dv = (e: PointerEvent) => { tek = { ...tek, x: e.clientX, y: e.clientY, cel: celSv(e.clientX, e.clientY, ot) }; setTyanuSv(tek); };
    const up = (e: PointerEvent) => {
      window.removeEventListener('pointermove', dv);
      window.removeEventListener('pointerup', up);
      document.body.classList.remove('rb-svyazyvaem');
      setTyanuSv(null);
      if (Math.hypot(e.clientX - x0, e.clientY - y0) < 6) return;   // просто щелчок по полосе
      const cel = celSv(e.clientX, e.clientY, ot);
      if (!cel) return;
      if (svyazi.some(x => x.pred === ot && x.work === cel)) { toast('Связь уже есть'); return; }
      if (krugIz(cel, ot)) { toast('Нельзя: получится замкнутый круг'); return; }
      void sozdatSv([{ pred: ot, work: cel }]).then(ids => { if (ids.length) toast('Связь: «' + cel + '» идёт после «' + ot + '»'); });
    };
    window.addEventListener('pointermove', dv);
    window.addEventListener('pointerup', up);
  }

  const gb = (r: IerStroka) => {
    const g = rasch.g.get(imyaSv(r));
    const d = Math.max(0, +(r.dlit ?? 0) || 0);
    if (!g) return <td className="rb-g" />;
    if (!d) return <td className="rb-g"><span className="rb-gb-net" style={{ left: x(g.es) }}>дней?</span></td>;
    return (
      <td className="rb-g">
        {g.zapas > 0 && <span className="rb-gb-zap" style={{ left: x(g.ef), width: g.zapas * PX }} />}
        <div className={'rb-gbar' + (g.krit ? ' krit' : '') + (tyanuSv && tyanuSv.cel === imyaSv(r) ? ' cel' : '')} data-gb={imyaSv(r)} style={{ left: x(g.es), width: d * PX }}
          onPointerDown={ev => svNachat(ev, imyaSv(r))}
          title={r.name + ': день ' + (g.es + 1) + '–' + g.ef + (g.krit ? ' · на критическом пути' : ' · запас ' + g.zapas + ' дн')
            + (can ? '. Потяните мышью к другой работе — она пойдёт после этой' : '')} />
        <span className={'rb-gb-p' + (g.krit ? ' krit' : '')} style={{ left: x(g.ef + g.zapas) + 4 }}>{d} дн{g.zapas > 0 ? ' · запас ' + g.zapas : ''}</span>
      </td>);
  };
  /** полоса раздела или группы — охват её работ */
  const gsPo = (stroki: IerStroka[], name: string) => {
    const vse = stroki.map(w => rasch.g.get(imyaSv(w))).filter((v): v is Rasch => !!v);
    if (!vse.length) return <td className="rb-g" />;
    const a = Math.min(...vse.map(v => v.es)), b = Math.max(...vse.map(v => v.ef));
    return <td className="rb-g"><div className="rb-gsum" style={{ left: x(a), width: Math.max(4, (b - a) * PX) }} title={name + ': день ' + (a + 1) + '–' + b} /></td>;
  };
  const gs = (i: number) => gsPo(R.slice(i + 1, konecPoddereva(R, i)), R[i].name);

  /** поля работы столбцами (вид «Параметры»): значение выглядит текстом, стрелка — при наведении;
      после признаков — столбец на каждую разбивку проекта: раздел, где стоит работа */
  const razbVida = vid === 'otch' ? razbivki : [];
  const polya = (r: IerStroka) => [...polyaVida.map(pl => {
    const v = znachPolya(r, pl.k);
    const opts = variantyPolya.get(pl.k) || [];
    const vse = v && !opts.includes(v) ? [v, ...opts] : opts;
    const pusto = pl.k === 'logika' ? 'типовые снизу вверх' : pl.k === 'stroka_gpr' ? 'сама себе строка' : '— не задано —';   // пустой порядок — как считается по умолчанию (бледным)
    if (pl.k === 'korpusa') return (
      <td key={pl.k} className={'rb-v rb-v-' + pl.k}>
        {r.work_id
          ? <input className={'rb-t' + (v ? '' : ' pusto')} defaultValue={v} key={r.klyuch + ':' + v} placeholder="все корпуса" disabled={!can} spellCheck={false}
              title={pl.title + (can ? '. Enter — сохранить; выделено несколько — у всех выделенных' : '')}
              onKeyDown={ev => { ev.stopPropagation(); if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); if (ev.key === 'Escape') { (ev.target as HTMLInputElement).value = v; (ev.target as HTMLInputElement).blur(); } }}
              onBlur={ev => { const nv = ev.target.value.split(',').map(x => x.trim()).filter(Boolean).join(','); if (nv !== v) void smenitPole(r, pl.k, nv); }} />
          : <span className="rb-v-net">сохраняется…</span>}
      </td>);
    return (
      <td key={pl.k} className={'rb-v rb-v-' + pl.k}>
        {r.work_id
          ? <select className={'rb-t' + (v ? '' : ' pusto')} value={v} disabled={!can}
              title={(pl.k === 'stroka_gpr' && v ? 'Строка ГПР: ' + v + '. ' : '') + pl.title + (can ? '. Выделено несколько — поменяется у всех выделенных' : '')}
              onChange={ev => void (ev.target.value === NOVOE ? novoeZnachenie(r, pl.k) : smenitPole(r, pl.k, ev.target.value))}>
              {pl.k !== 'raskatka' && pl.k !== 'kalendar' && <option value="">{pusto}</option>}
              {vse.map(o => <option key={o} value={o}>{pl.k === 'raskatka' ? RASK_LABEL[o] || o : pl.k === 'kalendar' ? KAL_LABEL[o] || o : o}</option>)}
              {'kod' in pl && <option value={NOVOE}>＋ новое значение…</option>}
              {pl.k === 'stroka_gpr' && can && <option value={NOVOE}>＋ новая строка ГПР…</option>}
            </select>
          : <span className="rb-v-net">сохраняется…</span>}
      </td>);
  }), ...razbVida.map(rz => {
    const v = r.work_id ? razdelRaboty(rz, r) : null;
    return (
      <td key={'rz' + rz.id} className="rb-v rb-v-rz">
        {v == null
          ? <span className="rb-v-net">{r.work_id ? 'загружается…' : 'сохраняется…'}</span>
          : <select className={'rb-t' + (v ? '' : ' pusto')} value={v} disabled={!can}
              title={'Этап в разбивке «' + rz.name + '»' + (can ? '. Выбор переносит работу в конец этапа; выделено несколько — все выделенные' : '')}
              onChange={ev => void perenestiVRazdel(rz, r, ev.target.value)}>
              <option value="">— без этапа —</option>
              {razdelyRz(rz).map(x => <option key={x.k} value={x.k}>{x.put}</option>)}
            </select>}
      </td>);
  })];
  const pustyePolya = () => [...polyaVida.map(pl => <td key={pl.k} className={'rb-v rb-v-' + pl.k} />),
    ...razbVida.map(rz => <td key={'rz' + rz.id} className="rb-v rb-v-rz" />)];


  /** «Дней» у строки ГПР (#119, правило 1): ручная длительность строки — обычным цветом, иначе по самой долгой
      работе — приглушённо; двойной щелчок — править, пусто — снова по работам. Даты — после «Пересчитать» */
  async function dnStrSohr(imya: string, value: string) {
    setDnStr(null);
    const s = strokiGpr.get(imya);
    if (!s || !can) return;
    const t = value.trim().replace(',', '.');
    if (t && (isNaN(+t) || +t <= 0)) { toast('Длительность строки — число дней больше нуля'); return; }
    const nov = t === '' ? null : +t;
    if (nov === s.dlit) return;
    const res = await call('patch', p('/api/projects/{pid}/stroki-gpr/{sid}', { pid: ctx.pid, sid: s.id }), { dlit_ruchnaya: nov });
    if (!res.ok) { toast(res.error.message); ctx.onError?.(res.error.message); return; }
    toast('Строка ГПР «' + imya + '»: ' + (nov == null ? 'длительность по самой долгой работе' : nov + ' дн вручную') + ' — даты обновит «Пересчитать»');
    await zagruzitRaboty();
    ctx.onChanged?.();
  }
  const dnStrYach = (g: Gruppa) => {
    const imya = g.val;
    const s = imya ? strokiGpr.get(imya) : undefined;
    if (!imya || !s) return <td className="rb-dn" />;
    const avto = g.idx.map(j => R[j].dlit).filter((d): d is number => d != null).reduce((m, d) => Math.max(m, d), -1);
    const ruch = s.dlit != null;
    return (
      <td className="rb-dn">
        {dnStr === imya
          ? <input className="rb-dn-i" defaultValue={ruch ? String(s.dlit) : ''} inputMode="decimal" placeholder={avto >= 0 ? String(avto) : ''}
              title="Длительность строки на этаж; пусто — по самой долгой работе. Enter — сохранить, Esc — отменить"
              ref={el => { if (el && document.activeElement !== el) { el.focus(); el.select(); } }}
              onKeyDown={ev => {
                ev.stopPropagation();
                if (ev.key === 'Enter') { ev.preventDefault(); (ev.target as HTMLInputElement).blur(); }
                else if (ev.key === 'Escape') { ev.preventDefault(); setDnStr(null); }
              }}
              onBlur={ev => { if (dnStr === imya) void dnStrSohr(imya, ev.target.value); }} />
          : <span className={'rb-dt' + (ruch ? '' : avto >= 0 ? ' avto' : ' net') + (can ? '' : ' ro')}
              title={(ruch ? 'Длительность строки ГПР задана вручную: ' + s.dlit + ' дн на этаж для всех её работ'
                : 'Длительность строки ГПР — по самой долгой работе в ней' + (avto >= 0 ? ' (' + avto + ' дн)' : ''))
                + (can ? '. Двойной щелчок — задать вручную; пусто — снова по работам' : '')}
              onMouseDown={ev => { if (!can || ev.ctrlKey || ev.metaKey || ev.shiftKey) return; ev.preventDefault(); ev.stopPropagation(); if (ev.detail >= 2) setDnStr(imya); }}>
              {ruch ? s.dlit : avto >= 0 ? avto : 'дней?'}</span>}
      </td>);
  };
  /** одна строка таблицы «Работ»: заголовок группы, работа (в т.ч. главная работа строки ГПР) или строка «новая группа» */
  const stroka_ = (st: Pokaz): React.ReactNode => {
            const pervVyb = novGr !== null && st.i != null && st.i === (vybVPoryadke()[0] ?? -1);
            const novaya = pervVyb ? (
              <tr key="nov-gr" className="rb-sec rb-grp rb-nov-gr">
                <td className="rb-ruchka" /><td className="rb-n" />
                <td className="rb-name">
                  <span className="rb-tgl">▾</span>
                  <input className="rb-nm-i sec" placeholder={'Новая группа «' + gDef.t + '» — название, Enter'} spellCheck={false}
                    ref={el => { if (el && document.activeElement !== el) el.focus(); }}
                    style={{ width: Math.max(80, nmW - 48) }} title="Enter — создать группу из выделенных работ, Esc — отменить"
                    onKeyDown={ev => {
                      ev.stopPropagation();
                      if (ev.key === 'Enter') { ev.preventDefault(); void novGruppa((ev.target as HTMLInputElement).value); }
                      else if (ev.key === 'Escape') { ev.preventDefault(); setNovGr(null); }
                    }}
                    onBlur={ev => { const v = ev.target.value.trim(); if (v) void novGruppa(v); else setNovGr(null); }} />
                </td>
                <td className="rb-dn" />
                {param ? pustyePolya() : <><td className="rb-pt" /><td className="rb-g" /></>}
              </tr>) : null;
            if (st.gr) {
              const g = st.gr, sv_ = svGr.has(g.k);
              return (
                <tr key={g.k} data-k={g.k} className={'rb-sec rb-grp' + (cel && cel.k === g.k ? ' cel-vnutr' : '')}>
                  <td className="rb-ruchka" /><td className="rb-n" />
                  <td className="rb-name">
                    <span className="rb-tgl" title={sv_ ? 'Развернуть группу' : 'Свернуть группу'}
                      onMouseDown={ev => { ev.stopPropagation(); setSvGr(m => { const n = new Set(m); if (n.has(g.k)) n.delete(g.k); else n.add(g.k); return n; }); }}>{sv_ ? '▸' : '▾'}</span>
                    <span className={'rb-nm-t sec' + (g.val == null ? ' pusto' : '')} title={gDef.t + ': ' + g.name}>{g.name}</span>
                    <span className="rb-grp-n" title="Работ в группе">{g.idx.length}</span>
                  </td>
                  {grup === 'stroka' ? dnStrYach(g) : <td className="rb-dn" />}
                  {param ? pustyePolya() : <><td className="rb-pt" />{gsPo(g.idx.map(j => R[j]), g.name)}</>}
                </tr>);
            }
            const i = st.i;
            const r = R[i], sec = deti[i], k = r.klyuch;
            const vyb = vybor.includes(k);
            const por = !sec && vybRaboty.length > 1 ? vybRaboty.indexOf(r) : -1;
            const glav = st.glav;   // работа — заголовок своей строки ГПР (после «Разбить»)
            const ur = glav ? 0 : G(r);
            const kl = (sec ? 'rb-sec' : 'rb-rab') + (glav ? ' rb-glav' : '') + (vyb ? ' vyb' : '') + (tyanemPod.has(k) ? ' tyanu' : '')
              + (cel && cel.k === k ? ' cel-' + cel.kuda : '') + (tyanuSv && !sec && tyanuSv.cel === imyaSv(r) ? ' sv-cel' : '');
            const svern = ie.svernuto.has(k);
            return (
              <Fragment key={k}>
              {novaya}
              <tr data-k={k} className={kl} onMouseDown={ev => naRyad(ev, k)}>
                <td className="rb-ruchka">{can && <span title={svoya ? 'Потяните строку мышью: выше, ниже или внутрь этапа' : 'Потяните в другую группу — работе поменяется ' + gDef.t.toLowerCase()}
                  onPointerDown={ev => { ev.preventDefault(); setTyanem(k); }}>⠿</span>}</td>
                <td className="rb-n">{i + 1}</td>
                <td className="rb-name">
                  <span className="rb-otstup" style={{ width: ur * 20 }} />
                  {sec
                    ? <span className="rb-tgl" title={svern ? 'Развернуть этап' : 'Свернуть этап'}
                        onMouseDown={ev => { ev.stopPropagation(); ie.svernut(k); }}>{svern ? '▸' : '▾'}</span>
                    : glav
                      ? <span className="rb-tgl" title={svGr.has(glav.k) ? 'Развернуть строку ГПР' : 'Свернуть строку ГПР'}
                          onMouseDown={ev => { ev.stopPropagation(); setSvGr(m => { const n = new Set(m); if (n.has(glav.k)) n.delete(glav.k); else n.add(glav.k); return n; }); }}>{svGr.has(glav.k) ? '▸' : '▾'}</span>
                      : <span className="rb-tgl" />}
                  {por >= 0 && <span className="rb-por-n" title="Место в цепочке при «Связать»">{por + 1}</span>}
                  {pravim === k
                    ? <input className={'rb-nm-i' + (sec ? ' sec' : '')} defaultValue={r.name} spellCheck={false}
                        ref={el => { if (el && document.activeElement !== el) { el.focus(); el.select(); } }}
                        style={{ width: Math.max(80, nmW - 48 - ur * 20 - (por >= 0 ? 24 : 0)) }}
                        title={'Enter — сохранить, Esc — отменить' + (svoya ? ', Tab / Shift+Tab — вложить / поднять' : '')}
                        onKeyDown={ev => {
                          ev.stopPropagation();
                          if (ev.key === 'Enter') { ev.preventDefault(); (ev.target as HTMLInputElement).blur(); }
                          else if (ev.key === 'Escape') { ev.preventDefault(); otmenaImeni.current = true; (ev.target as HTMLInputElement).blur(); }
                          else if (ev.key === 'Tab') {
                            ev.preventDefault();
                            if (!svoya && !grupMozhno) return;
                            const sh = ev.shiftKey;
                            (ev.target as HTMLInputElement).blur();
                            setVybor([k]); yakor.current = k;
                            if (svoya) { if (sh) vlevo([k]); else vpravo([k]); }
                            else window.setTimeout(() => { if (sh) vlevoGr(); else vpravoGr(); }, 0);
                          }
                        }}
                        onBlur={ev => zakonchitImya(k, ev.target.value)} />
                    : <span className={'rb-nm-t' + (sec || glav ? ' sec' : '')} style={{ maxWidth: Math.max(40, nmW - 42 - ur * 20 - (por >= 0 ? 24 : 0) - (glav ? 30 : 0)) }} title={r.name + (can ? ' — двойной щелчок или F2: изменить название' : '')}>{r.name}</span>}
                  {glav && <span className="rb-grp-n" title="Работ в строке ГПР (с самой работой)">{glav.idx.length}</span>}
                  {/* «⋯» — карточка работы, у правого края названия при наведении (отдельного столбца нет с 2026-10-16) */}
                  {!sec && r.work_id && <span className="rb-kart-b" title="Карточка работы: место, группа, корпуса, календарь, тепловой контур, связи"
                    onClick={() => ctx.onWorkCard?.(r.name)}>⋯</span>}
                </td>
                {glav ? dnStrYach(glav) : <td className="rb-dn">
                  {sec ? null : dnPravka && dnPravka.k === k
                    ? <input className="rb-dn-i" defaultValue={dnPravka.start} inputMode="decimal" title="Enter — сохранить и вниз, Esc — отменить"
                        ref={el => { if (el && document.activeElement !== el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); } }}
                        onKeyDown={ev => {
                          ev.stopPropagation();
                          const el = ev.target as HTMLInputElement;
                          if (ev.key === 'Enter') { ev.preventDefault(); void dnSohr(el.value, true); }
                          else if (ev.key === 'Tab') { ev.preventDefault(); void dnSohr(el.value, false); }
                          else if (ev.key === 'Escape') { ev.preventDefault(); dnZakryto.current = true; setDnPravka(null); }
                        }}
                        onBlur={ev => void dnSohr(ev.target.value, false)} />
                    : <span className={'rb-dt' + (dnAkt === k ? ' akt' : '') + (r.dlit == null ? ' net' : '') + (can ? '' : ' ro')}
                        title={can ? 'Длительность на типовой этаж (для «на корпус» — всего), дней. Щелчок — выбрать, затем наберите число; двойной щелчок или F2 — править' : 'Длительность на типовой этаж, дней'}
                        onMouseDown={ev => {
                          if (ev.ctrlKey || ev.metaKey || ev.shiftKey) return;   // с Ctrl/Shift — только выделение строки
                          ev.preventDefault(); ev.stopPropagation();
                          if (ev.detail >= 2) dnNachat(k, String(r.dlit ?? '')); else dnVybrat(k);
                        }}>{r.dlit ?? 'дней?'}</span>}
                </td>}
                {param ? (sec ? pustyePolya() : polya(r))
                  : <>{sec ? <><td className="rb-pt" /></> : <>{tegi(r, false)}</>}
                    {sec ? gs(i) : glav ? gsPo(glav.idx.map(j => R[j]), r.name) : gb(r)}</>}
              </tr>
              {vst && vst.posle === k && can && (
                <tr className="rb-chern">
                  <td className="rb-ruchka" /><td className="rb-n" />
                  <td className="rb-name">
                    <span className="rb-otstup" style={{ width: G(r) * 20 }} /><span className="rb-tgl" />
                    <input autoFocus className="rb-nm-i" placeholder="новая работа — Enter" spellCheck={false}
                      style={{ width: Math.max(80, nmW - 48 - G(r) * 20) }}
                      title="Enter — добавить и вписать следующую; Esc или пусто — закончить"
                      onKeyDown={ev => {
                        ev.stopPropagation();
                        if (ev.key === 'Escape') { ev.preventDefault(); setVst(null); return; }
                        if (ev.key === 'Enter') { ev.preventDefault(); const el = ev.target as HTMLInputElement; vstEnter(el.value); el.value = ''; }
                      }}
                      onBlur={ev => { if (!ev.target.value.trim()) setVst(cur => (cur && cur.posle === k ? null : cur)); }} />
                  </td>
                  <td className="rb-dn">{r.dlit ?? ''}</td>
                  {param
                    ? <td colSpan={polyaVida.length + razbVida.length} className="rb-v-pod">{'дни, раскатка и порядок — как у «' + r.name + '»'}</td>
                    : <><td className="rb-pt" /><td className="rb-g"><span className="rb-chern-pod">{'дни, раскатка и порядок — как у «' + r.name + '»'}</span></td></>}
                </tr>
              )}
              </Fragment>);
  };
  return (
    <div className="rb" ref={ekran} style={{ height: vys }}>
      <div className="rb-bar">
        <span className="rb-seg rb-strukt" title="Как сгруппировать работы: свои разбивки — этапы, которые задаёте вы (основная «Этапы» даёт этапы работам), остальные — группы по признаку работы">
          {/* порядок (владелец 2026-10-17): системные — основная «Этапы» и группировки по признаку (не удаляются),
              за ними свои разбивки (с ×) — рядом с «＋ своя разбивка» */}
          {(() => {
            const vse = razbivki.length ? razbivki : tekRazb ? [tekRazb] : [];
            const knopka = (rz: Razbivka) => {
            const otkryta = !!tekRazb && tekRazb.id === rz.id;
            return (
              <button key={rz.id} className={'svr' + (otkryta ? ' on' : '')} onClick={() => setGrup(RZ + rz.id)}
                onDoubleClick={() => { if (otkryta && !rz.osnovnaya) void pereimenovatRazbivku(rz); }}
                title={rz.osnovnaya ? 'Основная разбивка: верхний уровень — этапы работ, контур этапа → работам'
                  : 'Своя разбивка: заголовки и вложенность задаёте вы, на расчёт не влияет' + (otkryta && can ? '. Двойной щелчок — переименовать' : '')}>
                {rz.name}
                {otkryta && !rz.osnovnaya && can && <i title="Удалить разбивку (работы останутся)" onClick={ev => { ev.stopPropagation(); void udalitRazbivku(rz); }}>×</i>}
              </button>);
            };
            const svoi = vse.filter(rz => !rz.osnovnaya);
            return <>
              {vse.filter(rz => rz.osnovnaya).map(knopka)}
              {GRUPPY.map(g => <button key={g.k} className={grup === g.k ? 'on' : ''} onClick={() => setGrup(g.k)}>{g.t}</button>)}
              {svoi.length > 0 && <span className="rb-seg-sep" />}
              {svoi.map(knopka)}
            </>;
          })()}
          {can && <>
            <span className="rb-seg-sep" />
            {novRazb
              ? <input ref={novRazbInp} className="rb-nov-razb" placeholder="название, Enter" maxLength={60} spellCheck={false}
                  onKeyDown={ev => {
                    ev.stopPropagation();
                    if (ev.key === 'Escape') { ev.preventDefault(); setNovRazb(false); }
                    if (ev.key === 'Enter') { ev.preventDefault(); void sozdatRazbivku((ev.target as HTMLInputElement).value); }
                  }}
                  onBlur={ev => { if (!ev.target.value.trim()) setNovRazb(false); }} />
              : <button className="plus" onClick={() => setNovRazb(true)}
                  title="Своя разбивка: свои заголовки и порядок работ, например «Для заказчика» или «По захваткам». Работы, связи и дни общие на все разбивки">＋ своя разбивка</button>}
          </>}
        </span>
        <span className="rb-seg" title="Что показывать: порядок работ со связями и Гантом или настройки работ столбцами">
          <button className={vid === 'svyazi' ? 'on' : ''} onClick={() => setVid('svyazi')} title="Предшественники и диаграмма Ганта типового этажа">Гантт</button>
          <button className={vid === 'gpr' ? 'on' : ''} onClick={() => setVid('gpr')} title="Всё, что влияет на расчёт графика: раскатка, порядок этажей, календарь, корпуса">ГПР</button>
          <button className={vid === 'otch' ? 'on' : ''} onClick={() => setVid('otch')} title="Признаки для группировок и фильтров: группа работ, место, контур, свои разбивки">Отчёты</button>
        </span>
        <span className={'rb-sost' + (sostTxt === 'сохранено' ? ' ok' : sostTxt === 'не сохранилось' ? ' err' : '')}>{sostTxt}</span>
        {/* «Справочники» — справа (владелец 2026-10-17): работы проекта ↔ общий справочник «Глобально» */}
        {ctx.canPlan && <button className="sec rb-sprav" title="Работы текущего проекта и общего справочника рядом: переносить в обе стороны"
          onClick={ev => { const r = ev.currentTarget.getBoundingClientRect(); setSprOkno({ ...yakorPop(ev), y: r.bottom + 4 }); }}>Справочники</button>}
        {sprOkno && <Spravochniki ctx={ctx} at={sprOkno} onClose={() => setSprOkno(null)} />}
        <span className="rb-spr" title="Enter — связать выделенные (или править «Дней»), Tab — вложить в этап, Shift+Tab — поднять, F2 — изменить название, Esc — снять выделение. Новая строка берёт дни, раскатку и порядок у предыдущей работы этапа">?</span>
      </div>
      <div className="rb-knopki">
        {/* «＋ Этап» — первой, слева от поиска работы (владелец 2026-10-17) */}
        {can && grup !== 'net' && <>
          <button onClick={() => (svoya ? razdel() : nachatGruppu())} disabled={!vybRaboty.length}
              title={svoya ? 'Новый этап: выделенные работы войдут в него'
                : grup === 'stroka' ? 'Новая строка ГПР: выделенные работы объединятся в одну позицию графика с одинаковыми сроками'
                : 'Новая группа: название добавится в «Списки», выделенные работы войдут в неё'}>{svoya ? '＋ Этап' : grup === 'stroka' ? '＋ Строка' : '＋ Группа'}</button>
        </>}
        {/* «Разбить» — на всех видах «Работ» (владелец 2026-10-26): работа становится строкой ГПР, под ней — подработы */}
        {can && <>
          <button onClick={() => void razbit()} disabled={vybRaboty.length !== 1}
              title="Разбить выделенную работу на подработы: она станет строкой ГПР, под ней — строка ввода новой подработы (Enter — завести и ввести следующую, Esc — закончить); у каждой подработы свой факт и объём">⫶ Разбить</button>
          <span className="rb-raz" />
        </>}
        <span className="rb-poisk-rab">
          <input value={poisk} placeholder={otbor.size ? 'отмечено ' + otbor.size + ' — найти ещё…' : 'Найти работу…'} spellCheck={false}
            title="Список работ: начните вводить — останутся подходящие; щелчок — выделить работу; Esc — сбросить"
            onFocus={() => setPoiskSp(-1)}
            onBlur={ev => { if ((ev.relatedTarget as HTMLElement | null)?.closest?.('.rb-poisk-spisok')) { ev.target.focus(); return; } window.setTimeout(() => setPoiskSp(null), 150); }}
            onChange={ev => { setPoisk(ev.target.value); setPoiskSp(-1); }}
            onKeyDown={ev => {
              ev.stopPropagation();
              const n = spisokPoiska.length;
              if (ev.key === 'Escape') { if (poiskSp != null) setPoiskSp(null); else sbrositPoisk(); (ev.target as HTMLInputElement).blur(); return; }
              if (ev.key === 'ArrowDown') { ev.preventDefault(); setPoiskSp(v => Math.min(n - 1, (v ?? -2) + 1)); return; }
              if (ev.key === 'ArrowUp') { ev.preventDefault(); setPoiskSp(v => Math.max(poTekstu ? -1 : 0, (v ?? 0) - 1)); return; }
              if (ev.key === 'Enter') {
                ev.preventDefault();
                if (poiskSp != null && poiskSp >= 0 && spisokPoiska[poiskSp] != null) vybratIzPoiska(spisokPoiska[poiskSp]);
                else if (poTekstu && poTekstu.length) vydelitNaidennye();
              }
            }} />
          {poiskSp != null && (
            <div className="rb-poisk-spisok" onMouseDown={ev => ev.preventDefault()}>
              {poTekstu && poTekstu.length > 0 && (
                <div className={'rb-ps-v vse' + (poiskSp === -1 ? ' on' : '')} onClick={vydelitNaidennye}>
                  {poTekstu.every(i => otbor.has(R[i].klyuch)) ? 'Снять все найденные' : 'Отметить все найденные'} ({poTekstu.length})</div>)}
              {spisokPoiska.map((i, k) => (
                <label key={R[i].klyuch} className={'rb-ps-v' + (poiskSp === k ? ' on' : '')} onMouseEnter={() => setPoiskSp(k)}>
                  <input type="checkbox" tabIndex={-1} checked={otbor.has(R[i].klyuch)} onChange={() => vybratIzPoiska(i)} />
                  <i>{i + 1}</i>{R[i].name}</label>))}
              {otbor.size > 0 && <div className="rb-ps-niz"><span>отмечено {otbor.size}</span><a onClick={() => primenitOtbor(new Set())}>снять все</a></div>}
              {!spisokPoiska.length && <div className="rb-ps-net">ничего не нашлось</div>}
            </div>)}
          {naideno && <>
            <span className="rb-poisk-n">{otbor.size ? 'отмечено ' + otbor.size : naideno.size ? 'найдено ' + naideno.size : 'ничего'}</span>
            {!otbor.size && naideno.size > 0 && <button onClick={vydelitNaidennye}
              title="Выделить все найденные работы (или Enter в поле поиска) — дальше можно поменять признак в «ГПР» / «Отчётах» сразу у всех">Выделить найденные</button>}
            <button className="rb-poisk-x" onClick={sbrositPoisk} title="Сбросить поиск и отметки">×</button>
          </>}
        </span>
        <span className="rb-raz" />
        <button onClick={() => svernutVse(true)} title="Свернуть все этапы и группы — видны только заголовки"
          disabled={svoya ? !R.some((r, i) => deti[i] && !ie.svernuto.has(r.klyuch)) : !gruppy.some(g => !svGr.has(g.k))}>⊟ Свернуть</button>
        <button onClick={() => svernutVse(false)} title="Развернуть все этапы и группы"
          disabled={svoya ? !R.some((r, i) => deti[i] && ie.svernuto.has(r.klyuch)) : !gruppy.some(g => svGr.has(g.k))}>⊞ Развернуть</button>
        {can && (svoya || grup === 'net' || grupMozhno) && <>
          <span className="rb-raz" />
          {grup !== 'net' && <>
            <button onClick={vlevoV} disabled={!(svoya ? vybIdx.some(i => !deti[i] && R[i].glubina > 0) : vybRaboty.length > 0)}
              title={svoya ? 'Вынести выделенные работы из этапа — клавиша Shift+Tab' : 'Убрать выделенные работы из группы («— не задано —») — клавиша Shift+Tab'}>⇤ Влево</button>
            <button onClick={vpravoV} disabled={!(svoya ? vpravoMozhno : vybRaboty.length > 0)}
              title={svoya ? 'Положить выделенные работы в этап выше — клавиша Tab' : 'Работу без группы — в группу выше — клавиша Tab'}>Вправо ⇥</button>
            <span className="rb-raz" />
          </>}
          <button onClick={() => (svoya ? peremestit(true) : peremestitGr(true))} disabled={svoya ? mozhnoVverh(R, vybor) < 0 : sosedGr(true) < 0}
            title="Переставить выделенное выше (в пределах своего этапа)">↑ Выше</button>
          <button onClick={() => (svoya ? peremestit(false) : peremestitGr(false))} disabled={svoya ? !mozhnoVniz(R, vybor) : sosedGr(false) < 0}
            title="Переставить выделенное ниже (в пределах своего этапа)">↓ Ниже</button>
        </>}
        {can && <>
          <span className="rb-raz" />
          <button className="glav" onClick={() => void svyazat()} disabled={vybRaboty.length < 2}
            title="Связать выделенные работы цепочкой «окончание → начало» в том порядке, в каком вы их выделяли (Ctrl+щелчок). Номер у строки — её место в цепочке. Клавиши: Enter или Ctrl+F2; отменить — Ctrl+Z">🔗 Связать</button>
          <button onClick={() => void razorvat()} disabled={!razorvatMozhno} title="Убрать связи между выделенными работами">Разорвать</button>
          <span className="rb-raz" />
          <button className="opasno" onClick={() => void udalit()} disabled={!est} title="Удалить выделенные строки (работы уйдут из графика)">Удалить</button>
        </>}
        {/* последователи — без столбца (владелец 2026-10-16): у одной выделенной работы — номерами здесь,
            щелчок переходит к работе; на Ганте они видны стрелками */}
        {!param && vybRaboty.length === 1 ? (() => {
          const nm = imyaSv(vybRaboty[0]);
          const posle = svyazi.filter(e => e.pred === nm);
          return (
            <span className="rb-vyb-info rb-posle">
              {posle.length ? 'Последователи:' : 'Последователей нет'}
              {posle.map(e => (
                <span key={e.id} className="rb-teg" title={e.work + ' — щелчок: перейти к работе'}
                  onClick={() => {
                    const i = R.findIndex(r => imyaSv(r) === e.work);
                    if (i < 0) return;
                    setVybor([R[i].klyuch]); yakor.current = R[i].klyuch;
                    document.querySelector('tr[data-k="' + R[i].klyuch + '"]')?.scrollIntoView({ block: 'center' });
                  }}><i>{nomer(e.work)}</i></span>))}
            </span>);
        })() : null}
      </div>
      <div className="rb-wrap" ref={wrap}>
        <div className={'rb-tw' + (vybRaboty.length > 1 ? ' mnogo' : '') + (param ? ' vid-param' : ' vid-svyazi')} ref={tw}
          style={{ '--gstep': (gShag * PX) + 'px', '--gots': OTS + 'px', '--rb-nm-w': nmW + 'px' } as React.CSSProperties}>
          <table>
            <thead><tr>
              <th className="rb-ruchka" />
              <th className="rb-n">№</th>
              <th className="rb-name">Работа<span className="rb-nm-resize" title="Потяните, чтобы изменить ширину столбца; двойной щелчок — обычная ширина"
                onPointerDown={nmTyanut} onDoubleClick={() => setNmW(NM_W)} /></th>
              <th className="rb-dn mass" title="Длительность на типовой этаж (для «на корпус» — всего), дней. Можно поменять сразу у всех выделенных работ">Дней</th>
              {param && polyaVida.map(pl => <th key={pl.k} className={'rb-v mass rb-v-' + pl.k} title={pl.title + '. Можно поменять сразу у всех выделенных работ'}>{pl.t}</th>)}
              {param && razbVida.map(rz => <th key={'rz' + rz.id} className="rb-v mass rb-v-rz"
                title={(rz.osnovnaya ? 'Основная разбивка' : 'Своя разбивка') + ' «' + rz.name + '»: этап, в котором стоит работа. Выбор переносит работу в конец этапа; можно сразу у всех выделенных'}>{rz.name}</th>)}
              {!param && <>
              <th className="rb-pt-h" title="Работы, после окончания которых начинается эта. «＋» — добавить, «×» — убрать">Предшественники</th>
              <th className="rb-gh" style={{ minWidth: gw, width: gw }}
                title="Гант одного типового этажа: дни от начала цикла. Начало работы — позднее окончание её предшественников">
                Типовой этаж, дни · <span className="rb-krit-leg" title="Работы без запаса: задержка любой из них сдвигает окончание этажа">критический путь {rasch.kritDlit} дн</span>
                <span className="rb-gh-pod">приближённо по одному этажу; точный график — на «Планировании»</span>
                <div className="rb-gtk">{shkala}</div>
              </th></>}
            </tr></thead>
            <tbody>
              {pokaz.map((st, jSt) => {
                const uzel = stroka_(st);
                if (jSt !== podrMesto || !podr) return uzel;
                return (
                  <Fragment key="podr-mesto">
                    {uzel}
                    <tr key="nov-podr" className="rb-rab rb-nov-podr">
                      <td className="rb-ruchka" /><td className="rb-n" />
                      <td className="rb-name">
                        <span className="rb-otstup" style={{ width: podrOtstup }} /><span className="rb-tgl" />
                        <input className="rb-nm-i" placeholder="Название подработы — Enter" spellCheck={false}
                          ref={el => { if (el && document.activeElement !== el) el.focus(); }}
                          style={{ width: Math.max(80, nmW - 48 - podrOtstup) }}
                          title="Enter — завести подработу и ввести следующую; пустой Enter или Esc — закончить"
                          onKeyDown={ev => {
                            ev.stopPropagation();
                            const el = ev.target as HTMLInputElement;
                            if (ev.key === 'Enter') {
                              ev.preventDefault();
                              if (el.value.trim()) { dobavitPodrabotu(el.value); el.value = ''; } else setPodr(null);
                            } else if (ev.key === 'Escape') { ev.preventDefault(); setPodr(null); }
                          }}
                          onBlur={ev => { if (ev.target.value.trim()) dobavitPodrabotu(ev.target.value); setPodr(null); }} />
                      </td>
                      <td className="rb-dn" />
                      {param ? pustyePolya() : <><td className="rb-pt" /><td className="rb-g" /></>}
                    </tr>
                  </Fragment>);
              })}
              {chern && (
                <tr className="rb-chern">
                  <td className="rb-ruchka" /><td className="rb-n">{R.length + 1}</td>
                  <td className="rb-name">
                    <span className="rb-otstup" style={{ width: (R.length ? R[R.length - 1].glubina : 0) * 20 }} /><span className="rb-tgl" />
                    <input ref={chernInp} className="rb-nm-i" placeholder="название работы — Enter" spellCheck={false}
                      style={{ width: Math.max(80, nmW - 48 - (R.length ? R[R.length - 1].glubina : 0) * 20) }}
                      title="Enter — добавить и перейти к следующей строке; Esc — убрать строку"
                      onKeyDown={ev => {
                        ev.stopPropagation();
                        if (ev.key === 'Escape') { setChern(false); return; }
                        if (ev.key === 'Enter') { ev.preventDefault(); chernEnter(); }
                      }}
                      onBlur={ev => { if (!ev.target.value.trim()) window.setTimeout(() => { if (document.activeElement !== chernInp.current) setChern(false); }, 120); }} />
                  </td>
                  <td className="rb-dn">{poslRabota?.dlit ?? ''}</td>
                  {param
                    ? <td colSpan={polyaVida.length + razbVida.length} className="rb-v-pod">{poslRabota ? 'дни, раскатка и порядок — как у «' + poslRabota.name + '»' : ''}</td>
                    : <><td className="rb-pt" />
                      <td className="rb-g"><span className="rb-chern-pod">{poslRabota ? 'дни, раскатка и порядок — как у «' + poslRabota.name + '»' : ''}</span></td></>}
                </tr>
              )}
              {can && (
                <tr className="rb-add">
                  <td className="rb-ruchka" /><td className="rb-n" />
                  <td className="rb-name"><button className="rb-add-kn" title="Добавить строку работы в конец таблицы"
                    onClick={() => { if (chern) chernInp.current?.focus(); else setChern(true); }}>＋ Работа</button></td>
                  <td className="rb-dn" />{param ? pustyePolya() : <><td className="rb-pt" /><td className="rb-g" /></>}
                </tr>
              )}
            </tbody>
          </table>
          {tyanuSv && (
            <svg className="rb-sv-liniya" width="100%" height="100%">
              <line x1={tyanuSv.x0} y1={tyanuSv.y0} x2={tyanuSv.x} y2={tyanuSv.y} />
              <circle cx={tyanuSv.x} cy={tyanuSv.y} r={4} />
            </svg>)}
          <svg className="rb-sloj" ref={svg} />
        </div>
      </div>
      <div className="rb-poyasn">
        <b>Выделить работу</b> — щелчок по строке, следующую в цепочку — Ctrl+щелчок, подряд — Shift+щелчок; <b>снять выделение</b> — Esc.
        {can && <> <b>Связать выделенные</b> — Enter или Ctrl+F2, отменить — Ctrl+Z. {svoya ? <><b>В этап / из этапа</b> — Tab / Shift+Tab или кнопки «Вправо» / «Влево»; этапы — только верхнего уровня, «＋ Этап» собирает в новый этап выделенные работы; строку можно потянуть за ⠿.</>
            : grup === 'net' ? <>Без группировки — плоский список в порядке «{osnName}»; «Выше» / «Ниже» — в пределах раздела «{osnName}».</>
            : grup === 'stroka' ? <><b>Строка ГПР</b> — работы, которые в графике идут одной позицией с одинаковыми сроками на этаже (например, все стояки). «＋ Строка» объединяет выделенные работы, «Разбить» делает выделенную работу строкой и заводит в ней подработы со своим фактом и объёмом, ⠿ — перенести работу в другую строку, Shift+Tab / «Влево» — вернуть работу в отдельную строку. В одной строке раскатка и корпуса должны совпадать; порядок этажей, календарь и признак ТК у работ строки общие — правка у одной меняет у всех.</>
            : <><b>Поменять {gDef.t.toLowerCase()}</b> — потяните работу за ⠿ на заголовок другой группы или на работу в ней; выделенные вместе с ней получат то же значение. Tab / «Вправо» — работу без группы в группу выше, Shift+Tab / «Влево» — убрать из группы, «＋ Группа» — новая группа для выделенных.</>}
          {' '}<b>Изменить название</b> — двойной щелчок или F2. <b>Дни</b> — щёлкните ячейку и наберите число; <span className="rb-leg-mass" />голубой столбец «Дней» меняется сразу у всех выделенных работ.</>}
        {' '}<b>Справа — Гант одного типового этажа:</b> полоса = работа длиной в свои дни, начало — позднее окончание её предшественников.
        {' '}<b>Красным — критический путь</b>: работы без запаса, задержка любой из них сдвигает окончание этажа. У остальных пунктир и «запас N» — на сколько дней работа может съехать, не сдвинув этаж.
        {' '}Яркая стрелка — предшественник, который держит начало; бледные — остальные. Мягкие связи (только порядок этажей) срок не держат и в расчёт не идут.
        {' '}Столбцы «Предшественники» и «Последователи» — теги: номер строки и название{can ? '; «×» убирает связь, «＋» — выбрать работу из списка (в списке нет самой работы, уже стоящих и тех, что дали бы круг)' : ''}. Щелчок по тегу — настройка связи: тип (ОН, НН, ОО, НО), сдвижки, мягкая; из окна — и карточка работы.
        {' '}Полный график по корпусам и этажам — на вкладке «Планирование».
        {' '}<b>Разбивки</b> — свои наборы разделов: работы, связи и дни общие, у каждой разбивки свои заголовки и порядок; на расчёт влияет только основная «{osnName}» (раздел первого уровня = этап).
        {svoya && tekRid != null && ' Открыта своя разбивка: новая работа здесь встанет и в остальные (в конец), удаление работы уберёт её из графика, удаление заголовка — только здесь.'}
        {vid === 'otch' && razbivki.length > 1 && ' В столбцах разбивок — этап, где стоит работа; выбор переносит её в конец этапа той разбивки.'}
      </div>
      {okno && oknoE && <OknoSvyazi key={okno.id + ':' + okno.n} e={oknoE} at={okno.at} can={can} sbros={svSbros} posle={okno.posle}
        onPole={(pole, v) => pravSv(oknoE.id, pole, v)}
        onUbrat={() => { setOkno(null); void snyatSv(oknoE); }}
        onKarta={imya => { setOkno(null); ctx.onWorkCard?.(imya); }}
        onZakryt={() => setOkno(null)} />}
      {tost && <div className="rb-toast">{tost}</div>}
    </div>
  );
}
